import DOMPurify from 'dompurify';
import FeatureLockScreen from '../../components/apple-ui/FeatureLockScreen';
import { T09nLoaderOverlay } from '../../components/common/T09nLoader';
import ReferralToast from '../../components/apple-ui/ReferralToast';
import { shouldShowExportReferral, markExportReferralShown, shouldShowFirstFeatureReferral, markFirstFeatureUsed, EXPORT_MSG, FIRST_FEATURE_MSG } from '../../utils/referralTriggers';
import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayoutApple, CardApple, ButtonApple, ModalApple, InputApple, BadgeApple, GlobalContextBarApple } from '../../components/apple-ui';
import InstructionsPanel from '../../components/InstructionsPanel';
import ContextSelectorModal from '../../components/ContextSelectorModal';
import ActiveContextBadgesApple from '../../components/apple-ui/ActiveContextBadgesApple';
import EditableSegment from '../../components/EditableSegment';
import debounce from 'lodash/debounce';
import axios from 'axios';
import Papa from 'papaparse';
import { useTheme } from '../../contexts/ThemeContext';
import { useGlobalContext } from '../../contexts/GlobalContextProvider';
import { usePlan } from '../../contexts/PlanContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { 
  Upload, FolderOpen, Play, Save, Download, Settings, ChevronDown, Search, Filter,
  CheckCircle, AlertCircle, RotateCw, ArrowLeft, Plus, FileText, Database, BookOpen,
  Book, Zap, Clock, X, Check, ChevronLeft, ChevronRight, Loader2, RefreshCw, Copy,
  Eye, EyeOff, ArrowRight, Replace, ArrowDown, ArrowUp, Merge, CheckCheck, SkipForward,
  Keyboard, Table, Palette, Scissors, History, Target, Sparkles, GitMerge, Split,
  Lock, Unlock, Percent, Type, Hash, Languages, HelpCircle, Lightbulb, ToggleLeft,
  ToggleRight, Square, CheckSquare, FileUp, Trash2, Info, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { parseDocxWithColors } from '../../utils/docxColorParser';
import { parseXlsxWithColors } from '../../utils/xlsxColorParser';
import { parsePptxWithColors } from '../../utils/pptxColorParser';
import PDFDisclaimer from '../../components/PDFDisclaimer';
import mammoth from 'mammoth';
import PPTDisclaimer from '../../components/PPTDisclaimer';
import ExportFeedbackModal from '../../components/ExportFeedbackModal';
import '../../styles/apple-design-system.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DOC_API = `${API_URL}/api/documents`;
const PRO_API = `${API_URL}/api/pro`;

// Formats with backend HTML preview support (split-panel live preview)
const PREVIEW_FORMATS = new Set(['docx', 'xlsx', 'xls', 'pdf', 'txt', 'xliff', 'xlf', 'mqxliff', 'sdlxliff', 'txml', 'pptx']);

// Safely coerce a FastAPI `detail` (which may be a string OR a structured object,
// e.g. the language_mismatch dict) into a plain string for toast/notification use.
// Passing a raw object to toast.error() makes React throw "Objects are not valid
// as a React child", which crashes the whole CAT tool to a white screen.
function detailToMessage(detail, fallback = 'Something went wrong') {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => detailToMessage(d, '')).filter(Boolean).join('; ') || fallback;
  }
  if (typeof detail === 'object') {
    // FastAPI validation errors use { msg }, our custom errors use { message }
    return detail.message || detail.msg || detail.error || fallback;
  }
  return String(detail);
}

// Helper: apply source or target text to a preview DOM element
function _applySegmentToPreview(el, seg, mode, srcMap) {
  if (mode === 'source') {
    const src = srcMap[seg.id];
    if (src !== undefined) el.innerHTML = src;
  } else {
    if (seg.target?.trim()) {
      const targetText = seg.target.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
      const srcHtml = srcMap[seg.id] || '';
      const fmt = _extractSourceFormatting(srcHtml);
      el.innerHTML = fmt ? `<span style="${fmt}">${targetText}</span>` : targetText;
    } else {
      const src = srcMap[seg.id] || '';
      el.innerHTML = `<span style="opacity:0.35;font-style:italic">${src}</span>`;
    }
  }
}

// Extract dominant run-level formatting from source HTML so target text matches
function _extractSourceFormatting(srcHtml) {
  if (!srcHtml) return '';
  const styles = [];
  // Extract color from inline spans
  const colorMatch = srcHtml.match(/color:\s*([^;'">\s]+)/);
  if (colorMatch) styles.push(`color:${colorMatch[1]}`);
  // Extract font-size
  const sizeMatch = srcHtml.match(/font-size:\s*([^;'">\s]+)/);
  if (sizeMatch) styles.push(`font-size:${sizeMatch[1]}`);
  // Semantic formatting tags
  if (/<strong[\s>]/i.test(srcHtml) || /font-weight:\s*bold/i.test(srcHtml)) styles.push('font-weight:bold');
  if (/<em[\s>]/i.test(srcHtml) || /font-style:\s*italic/i.test(srcHtml)) styles.push('font-style:italic');
  if (/<u[\s>]/i.test(srcHtml) || /text-decoration:\s*underline/i.test(srcHtml)) styles.push('text-decoration:underline');
  return styles.join(';');
}

// Direct preview highlight — call from event handlers, no useEffect delay
function _applyPreviewHighlight(segId, previewDocEl, hlRef) {
  if (hlRef.current) {
    hlRef.current.style.outline = '';
    hlRef.current.style.backgroundColor = '';
    hlRef.current.style.borderRadius = '';
    hlRef.current.classList.remove('preview-highlight');
    hlRef.current = null;
  }
  if (!segId || !previewDocEl) return;
  const candidates = previewDocEl.querySelectorAll(`[data-segment-id="${segId}"]`);
  // Prefer visible element; PPTX overlay paras are visibility:hidden in source mode
  // but the preview-highlight CSS class overrides to visible — so use first match
  // if no visible candidate exists (the class will reveal it).
  let el = null;
  candidates.forEach(c => { if (!el && c.offsetParent !== null) el = c; });
  if (!el && candidates.length) el = candidates[0];
  if (!el) return;
  el.classList.add('preview-highlight');
  el.style.outline = '2px solid #2563eb';
  el.style.backgroundColor = 'rgba(37,99,235,0.1)';
  el.style.borderRadius = '3px';
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  hlRef.current = el;
}

const LANGUAGE_OPTIONS = [
  'English', 'Hindi', 'Gujarati', 'Tamil', 'Telugu', 'Kannada', 
  'Malayalam', 'Bengali', 'Marathi', 'Punjabi', 'Odia', 'Assamese', 'Urdu'
];

// Pre-built instructions for Instruction Selector
const PREBUILT_INSTRUCTIONS = [
  { id: 'formal-tone', text: 'Maintain formal tone throughout' },
  { id: 'preserve-brands', text: 'Preserve brand names and product names' },
  { id: 'keep-technical', text: 'Keep technical terms in English' },
  { id: 'gender-neutral', text: 'Use gender-neutral language' },
  { id: 'adapt-idioms', text: 'Adapt idioms and cultural references' },
  { id: 'consistent-terminology', text: 'Maintain consistent terminology' },
  { id: 'preserve-formatting', text: 'Preserve formatting and structure' },
  { id: 'industry-specific', text: 'Use industry-specific terminology' },
  { id: 'keep-numbers', text: 'Keep numbers and dates in original format' }
];
const PREBUILT_IDS = new Set(PREBUILT_INSTRUCTIONS.map(i => i.id));

const CATToolPageApple = ({ user, planInfo, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Theme and Global Context hooks
  const { isDarkMode } = useTheme();
  const { updateFromApiResponse, refreshPlanInfo } = usePlan();
  const catAccess = useFeatureAccess('cat');
  const {
    showContextModal,
    setShowContextModal,
    getContextIds,
    selectedProject: globalContextProject,
    selectedInstruction: globalContextInstruction,
    selectedGlossary: globalContextGlossary,
    selectedTM: globalContextTM,
    hasContext: hasGlobalContext,
    setSelectedInstruction,
    setSelectedProject,
  } = useGlobalContext();
  
  // Use global context project directly
  const selectedProject = globalContextProject;
  
  // Document state (project-related state removed - now using globalContextProject)
  const [segments, setSegments] = useState([]);
  const fileUploadedRef = React.useRef(false); // track if user uploaded a file this session
  const currentProjectIdRef = React.useRef(null); // track current project ID to detect real changes
  const [documentInfo, setDocumentInfo] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  
  // Language state
  const [sourceLang, setSourceLang] = useState('English');
  const [targetLang, setTargetLang] = useState('Hindi');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [projectLoadError, setProjectLoadError] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [catProgress, setCatProgress] = useState({ done: 0, total: 0, tmHits: 0, totalWords: 0 });
  const [scanningSegmentIds, setScanningSegmentIds] = useState(new Set());
  const [pipelineStage, setPipelineStage] = useState(0);
  const [activeSegment, setActiveSegment] = useState(null);
  const [focusedSegmentId, setFocusedSegmentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Note: showProjectDropdown removed - using GlobalContextBar
  
  // Saved/Confirmed segments state
  const [savedSegments, setSavedSegments] = useState(new Set());
  const [segmentConfirmStatus, setSegmentConfirmStatus] = useState({});
  
  // Find & Replace state
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(-1);
  
  // Jump to segment state
  const [showJumpDialog, setShowJumpDialog] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [jumpSegmentNumber, setJumpSegmentNumber] = useState('');
  
  // Live Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  // Document structure preview (DOCX only — fetched from backend)
  const [docPreviewHtml, setDocPreviewHtml] = useState(null);
  const [docPreviewLoading, setDocPreviewLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState('target'); // 'source' | 'target' — default target like Phrase
  const [previewZoom, setPreviewZoom] = useState(1.0);     // zoom level (0.5–2.0), matches Phrase zoom control
  const previewContainerRef = useRef(null); // outer scrollable div (click, scroll)
  const previewDocRef = useRef(null);       // inner doc div — innerHTML set directly (no dangerouslySetInnerHTML)
  const sourceHtmlMapRef = useRef({});      // seg_id → original source innerHTML
  const activeHighlightElRef = useRef(null); // currently highlighted preview element
  
  // QA state
  const [qaReport, setQaReport] = useState(null);
  const [runningQA, setRunningQA] = useState(false);
  const [showQADetails, setShowQADetails] = useState(false);
  const [showExportFeedback, setShowExportFeedback] = useState(false);
  const [referralToast, setReferralToast] = useState(null);

  const triggerExportReferral = () => {
    if (shouldShowExportReferral()) {
      markExportReferralShown();
      setReferralToast({ message: EXPORT_MSG, buttons: 'dismiss' });
    }
  };
  const triggerFirstFeatureReferral = () => {
    if (shouldShowFirstFeatureReferral()) {
      markFirstFeatureUsed();
      setTimeout(() => setReferralToast(prev => prev || { message: FIRST_FEATURE_MSG, buttons: 'remind_later' }), 3000);
    }
  };

  // TM and Glossary state
  const [tmMatches, setTmMatches] = useState([]);
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [showAddTermModal, setShowAddTermModal] = useState(false);
  const [newTermForm, setNewTermForm] = useState({ source: '', target: '', notes: '' });
  const [activeSegmentGlossaryMatches, setActiveSegmentGlossaryMatches] = useState([]);
  const [loadingGlossary, setLoadingGlossary] = useState(false);
  const [showGlossaryModal, setShowGlossaryModal] = useState(false);
  const glossaryFileInputRef = useRef(null);
  
  // Professional TM state
  const [proTmMatches, setProTmMatches] = useState([]);
  const [loadingTmMatches, setLoadingTmMatches] = useState(false);
  const [showConcordance, setShowConcordance] = useState(false);
  const [concordanceQuery, setConcordanceQuery] = useState('');
  const [concordanceResults, setConcordanceResults] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [selectedTmEntryId, setSelectedTmEntryId] = useState(null);
  const [tmStats, setTmStats] = useState(null);
  
  // Segment workflow state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitSegmentId, setSplitSegmentId] = useState(null);
  const [splitPosition, setSplitPosition] = useState(0);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSegmentIds, setMergeSegmentIds] = useState([]);
  const [statusSummary, setStatusSummary] = useState(null);
  
  // Instruction sets state
  const [instructionSets, setInstructionSets] = useState([]);
  const [selectedInstructionSet, setSelectedInstructionSet] = useState(null);
  
  // NEW: All project instruction names (collected from all projects)
  const [allProjectInstructions, setAllProjectInstructions] = useState([]);
  
  // NEW: Translation mode state (Manual/Auto)
  const [translationMode, setTranslationMode] = useState('manual'); // 'manual' or 'auto'
  
  // NEW: Project Guide panel state
  const [showProjectGuide, setShowProjectGuide] = useState(true);
  const [projectGuideTab, setProjectGuideTab] = useState('instructions'); // 'instructions', 'howto', 'tips'
  const [editingInstructions, setEditingInstructions] = useState(false);
  const [editableInstructionText, setEditableInstructionText] = useState('');
  const [savingInstructions, setSavingInstructions] = useState(false);
  
  // NEW: Pre-built instructions checkboxes
  const [prebuiltInstructions, setPrebuiltInstructions] = useState({
    formalTone: false,
    keepTechnical: false,
    preserveBrands: false,
    genderNeutral: false,
    consistentTerminology: false,
    industryTerms: false,
    keepNumbers: false
  });
  const [customInstructions, setCustomInstructions] = useState('');

  // Panel visibility (persisted)
  const [leftPanelOpen, setLeftPanelOpen] = useState(() => {
    try { return localStorage.getItem('cat-left-panel') !== 'false'; } catch { return true; }
  });
  const [rightPanelOpen, setRightPanelOpen] = useState(() => {
    try { return localStorage.getItem('cat-right-panel') !== 'false'; } catch { return true; }
  });

  // NEW: Segment checkboxes for multi-select (merge)
  const [selectedSegmentIds, setSelectedSegmentIds] = useState(new Set());
  
  // NEW: TMX upload state
  const [tmxFiles, setTmxFiles] = useState([]);
  const [uploadingTmx, setUploadingTmx] = useState(false);
  const tmxFileInputRef = useRef(null);
  
  // NEW: Translation history state
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [translationHistory, setTranslationHistory] = useState([]);
  
  // File disclaimer states
  const [showPdfDisclaimer, setShowPdfDisclaimer] = useState(false);
  const [showPptDisclaimer, setShowPptDisclaimer] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  
  // Refs
  const fileInputRef = useRef(null);
  const segmentRefs = useRef({});
  const pendingSegmentUpdates = useRef({});

  // Ref to track segment edits without causing re-renders
  const segmentEditsRef = useRef({});

  // Debounced segment update - stores in ref first, then syncs to state less frequently
  const debouncedSegmentUpdate = useCallback(
    debounce((segmentId, newText, newHtml) => {
      // Store in ref immediately (no re-render)
      segmentEditsRef.current[segmentId] = { text: newText, html: newHtml };
      
      // Update state less frequently for status indicators only
      setSegmentConfirmStatus(prev => {
        if (prev[segmentId] === 'confirmed') {
          return { ...prev, [segmentId]: 'edited' };
        }
        return prev;
      });
      setSavedSegments(prev => {
        const newSet = new Set(prev);
        newSet.delete(segmentId);
        return newSet;
      });
      delete pendingSegmentUpdates.current[segmentId];
    }, 500),
    []
  );

  // Sync edits from ref to state (call this before save operations)
  const syncSegmentEdits = useCallback(() => {
    const edits = segmentEditsRef.current;
    if (Object.keys(edits).length === 0) return;
    
    setSegments(prev => prev.map(s => {
      const edit = edits[s.id];
      if (edit) {
        return { ...s, target: edit.text, targetHtml: edit.html, status: edit.text?.trim() ? 'edited' : 'empty' };
      }
      return s;
    }));
    segmentEditsRef.current = {};
  }, []);

  // Get current segment content (from ref if edited, otherwise from state)
  const getSegmentContent = useCallback((segment) => {
    const edit = segmentEditsRef.current[segment.id];
    if (edit) {
      return { target: edit.text, targetHtml: edit.html };
    }
    return { target: segment.target, targetHtml: segment.targetHtml };
  }, []);

  useEffect(() => {
    return () => { debouncedSegmentUpdate.cancel(); };
  }, [debouncedSegmentUpdate]);

  useEffect(() => {
    if (!translating || catProgress.total === 0) { setPipelineStage(0); return; }
    const iv = setInterval(() => setPipelineStage(prev => (prev + 1) % 4), 800);
    return () => clearInterval(iv);
  }, [translating, catProgress.total]);

  // Load project data when globalContextProject changes
  useEffect(() => {
    const projectId = globalContextProject?.project_id || globalContextProject?.id;
    if (projectId) {
      // Only reset fileUploadedRef if project actually changed
      if (currentProjectIdRef.current !== projectId) {
        currentProjectIdRef.current = projectId;
        fileUploadedRef.current = false;
      }
      loadProjectData(projectId);
    } else {
      // Clear project data if no project selected
      setSegments([]);
      setDocumentInfo(null);
      setGlossaryTerms([]);
      setProjectLoadError(null);
    }
  }, [globalContextProject?.id, globalContextProject?.project_id]);

  // Also check URL params on mount
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (projectId && !globalContextProject) {
      toast.info('Please select a project using the context bar above');
    }
  }, [searchParams, globalContextProject]);

  // Per-session failed-fetch counter keyed by instruction ID — stops infinite retries
  const instructionFetchAttemptsRef = useRef({});

  // Sync global context instruction with selectedInstructionSet for display in Project Guide
  // Dep is ?.id (primitive) not the object — prevents re-runs from new object refs with same ID
  useEffect(() => {
    const id = globalContextInstruction?.id;
    if (!id) {
      setSelectedInstructionSet(null);
      return;
    }

    if (PREBUILT_IDS.has(id)) {
      setSelectedInstructionSet({
        id,
        name: globalContextInstruction.name || '',
        instructions: globalContextInstruction.text || globalContextInstruction.description || ''
      });
      return;
    }

    const attempts = instructionFetchAttemptsRef.current[id] || 0;
    if (attempts >= 3) return; // session cap: stop after 3 failures

    const fetchInstructionContent = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/instructions/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          instructionFetchAttemptsRef.current[id] = 0;
          const data = await response.json();
          if (data.deleted) {
            setSelectedInstruction(null);
            setSelectedInstructionSet(null);
          } else {
            setSelectedInstructionSet({
              id: data.id || id,
              name: data.name || globalContextInstruction.name,
              instructions: data.instructions || data.content || ''
            });
          }
        } else {
          instructionFetchAttemptsRef.current[id] = attempts + 1;
          if (response.status === 503 || response.status === 404 || response.status === 401) {
            setSelectedInstruction(null);
            setSelectedInstructionSet(null);
          }
        }
      } catch (e) {
        console.error('Error fetching instruction:', e);
        setSelectedInstructionSet(globalContextInstruction);
      }
    };

    fetchInstructionContent();
  }, [globalContextInstruction?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note: fetchProjects removed - projects are managed by GlobalContextBar

  // Load project data (segments, glossary) - called when global context project changes
  const loadProjectData = async (projectId) => {
    if (!projectId) return;

    setProjectLoadError(null);
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        if (res.status === 404) {
          setSelectedProject(null);
          setProjectLoadError('Project not found.');
        }
        throw new Error(`Project not found (${res.status})`);
      }
      const data = await res.json();
      if (data.source_language) setSourceLang(data.source_language);
      if (data.target_language) setTargetLang(data.target_language);

      if (data.segments && data.segments.length > 0 && !fileUploadedRef.current) {
        setSegments(data.segments.map((seg, idx) => ({
          ...seg,
          id: seg.segment_id || seg.id || idx,
          status: seg.status || 'draft'
        })));
        setDocumentInfo({
          name: data.document_name || 'Uploaded Document',
          segmentCount: data.segments.length
        });
      }
      if (data.glossary) setGlossaryTerms(data.glossary);
    } catch (error) {
      setProjectLoadError(prev => prev || 'Failed to load project data. Please try again.');
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!selectedProject) { toast.error('Please select a project using the context bar above'); return; }

    const filename = file.name.toLowerCase();
    const allowedTypes = ['.docx', '.xlsx', '.txt', '.csv', '.pdf', '.pptx', '.ppt', '.xliff', '.xlf', '.sdlxliff', '.mqxliff', '.txml'];
    const ext = '.' + filename.split('.').pop();
    if (!allowedTypes.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    let fileFormat = 'docx';
    if (filename.endsWith('.xlsx')) fileFormat = 'xlsx';
    else if (filename.endsWith('.pdf')) fileFormat = 'pdf';
    else if (filename.endsWith('.pptx') || filename.endsWith('.ppt')) fileFormat = 'pptx';
    else if (filename.endsWith('.txt') || filename.endsWith('.csv')) fileFormat = 'txt';
    else if (['.xliff', '.xlf', '.sdlxliff', '.mqxliff', '.txml'].some(ext => filename.endsWith(ext)))
      fileFormat = filename.split('.').pop();

    if (fileFormat === 'pdf') {
      setPendingFile(file);
      setShowPdfDisclaimer(true);
      e.target.value = '';
      return;
    }
    if (fileFormat === 'pptx') {
      setPendingFile(file);
      setShowPptDisclaimer(true);
      e.target.value = '';
      return;
    }

    await processFileUpload(file, fileFormat);
    e.target.value = '';
  };

  const handleDisclaimerProceed = async () => {
    if (!pendingFile) return;
    const filename = pendingFile.name.toLowerCase();
    let fileFormat = 'docx';
    if (filename.endsWith('.pdf')) fileFormat = 'pdf';
    else if (filename.endsWith('.pptx') || filename.endsWith('.ppt')) fileFormat = 'pptx';
    setShowPdfDisclaimer(false);
    setShowPptDisclaimer(false);
    await processFileUpload(pendingFile, fileFormat);
    setPendingFile(null);
  };

  const handleDisclaimerCancel = () => {
    setShowPdfDisclaimer(false);
    setShowPptDisclaimer(false);
    setPendingFile(null);
  };

  const processFileUpload = async (file, fileFormat) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      if (fileFormat === 'docx') {
        let parsedSegments;
        let hasColorFormatting = false;
        try {
          parsedSegments = await parseDocxWithColors(file);
          hasColorFormatting = parsedSegments.some(s => s.hasColorFormatting);
        } catch (colorParseErr) {
          toast.error('Failed to parse DOCX file');
          setLoading(false);
          return;
        }
        
        fileUploadedRef.current = true;
        setSegments(parsedSegments.map(seg => ({
          id: seg.id,
          source: seg.source,
          sourceHtml: seg.sourceHtml,
          target: '',
          targetHtml: '',
          status: 'empty',
          formatting: seg.formatting || {},
          hasColorFormatting: seg.hasColorFormatting
        })));
        
        setDocumentInfo({ 
          name: file.name, 
          segmentCount: parsedSegments.length, 
          format: file.name.toLowerCase().endsWith('.xlsx') ? 'xlsx' : 
                  file.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 
                  file.name.toLowerCase().endsWith('.pptx') ? 'pptx' : 'docx', 
          hasColorFormatting 
        });
        setUploadedFileName(file.name);
        setSavedSegments(new Set());
        setSegmentConfirmStatus({});
        setQaReport(null);
        
        toast.success(`Loaded ${parsedSegments.length} segments from ${file.name}`);
        if (hasColorFormatting) {
          toast.info('Color formatting detected! Colors will be preserved in export.', {
            icon: <Palette className="text-purple-500" size={18} />
          });
        }

        const formData = new FormData();
        formData.append('file', file);
        try {
          const response = await axios.post(`${DOC_API}/upload`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
          });
          setDocumentInfo(prev => ({ ...prev, documentId: response.data.document_id }));
        } catch (uploadErr) { console.warn('Backend upload for export failed:', uploadErr); }
      } else if (fileFormat === 'xlsx') {
        // Parse XLSX with frontend color extraction
        let parsedSegments;
        let hasColorFormatting = false;
        try {
          parsedSegments = await parseXlsxWithColors(file);
          hasColorFormatting = parsedSegments.some(s => s.hasColorFormatting);
        } catch (e) {
          console.warn('XLSX color parse failed, falling back to backend', e);
          parsedSegments = null;
        }

        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${DOC_API}/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        const { document_id, segments: docSegments, metadata } = response.data;

        console.log('[XLSX merge] sample parsed:', parsedSegments?.[0]);
        // Merge frontend colors with backend segments using text matching
        const colorMap = {};
        if (parsedSegments) {
          parsedSegments.forEach(ps => { colorMap[ps.source.trim()] = ps; });
        }
        const finalSegments = docSegments.map((seg, i) => {
          const match = parsedSegments && (parsedSegments[i] || colorMap[seg.source?.trim()]);
          return {
            ...seg,
            status: 'empty',
            sourceHtml: match?.sourceHtml || seg.sourceHtml || seg.source,
            targetHtml: '',
            hasColorFormatting: match?.hasColorFormatting || false
          };
        });

        setSegments(finalSegments);
        setDocumentInfo({
          name: file.name, segmentCount: metadata.segment_count || docSegments.length,
          format: 'xlsx', documentId: document_id, isExcel: true,
          sheetCount: metadata.sheet_count, hasColorFormatting
        });
        setUploadedFileName(file.name);
        setSavedSegments(new Set()); setSegmentConfirmStatus({}); setQaReport(null);
        toast.success(`Loaded ${finalSegments.length} segments from ${file.name}`);
        if (hasColorFormatting) toast.info('Color formatting detected!');

      } else if (fileFormat === 'pptx') {
        // Parse PPTX with frontend color extraction
        let parsedSegments;
        let hasColorFormatting = false;
        try {
          parsedSegments = await parsePptxWithColors(file);
          hasColorFormatting = parsedSegments.some(s => s.hasColorFormatting);
        } catch (e) {
          console.warn('PPTX color parse failed, falling back to backend', e);
          parsedSegments = null;
        }

        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${DOC_API}/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        const { document_id, segments: docSegments, metadata } = response.data;

        const finalSegments = parsedSegments && parsedSegments.length === docSegments.length
          ? docSegments.map((seg, i) => ({
              ...seg,
              status: 'empty',
              sourceHtml: parsedSegments[i]?.sourceHtml || seg.sourceHtml || seg.source,
              targetHtml: '',
              hasColorFormatting: parsedSegments[i]?.hasColorFormatting || false,
              formatting: { ...seg.formatting, ...(parsedSegments[i]?.formatting || {}) }
            }))
          : docSegments.map(seg => ({ ...seg, status: 'empty', sourceHtml: seg.sourceHtml || seg.source, targetHtml: '' }));

        setSegments(finalSegments);
        setDocumentInfo({
          name: file.name, segmentCount: metadata.segment_count || docSegments.length,
          format: 'pptx', documentId: document_id, isPptx: true,
          slideCount: metadata.slide_count, hasColorFormatting
        });
        setUploadedFileName(file.name);
        setSavedSegments(new Set()); setSegmentConfirmStatus({}); setQaReport(null);
        toast.success(`Loaded ${finalSegments.length} segments from ${file.name}`);
        if (hasColorFormatting) toast.info('Color formatting detected!');

      } else {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${DOC_API}/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        const { document_id, segments: docSegments, metadata } = response.data;
        
        fileUploadedRef.current = true;
        setSegments(docSegments.map(seg => ({
          ...seg,
          status: 'empty',
          sourceHtml: seg.sourceHtml || seg.source,
          targetHtml: ''
        })));
        
        setDocumentInfo({
          name: file.name,
          segmentCount: metadata.segment_count || docSegments.length,
          format: fileFormat,
          documentId: document_id,
          isExcel: metadata.is_excel,
          isPdf: metadata.is_pdf,
          sheetCount: metadata.sheet_count,
          pageCount: metadata.page_count,
          hasTableStructure: metadata.has_tables
        });
        setUploadedFileName(file.name);
        setSavedSegments(new Set());
        setSegmentConfirmStatus({});
        setQaReport(null);
        
        toast.success(`Loaded ${docSegments.length} segments from ${file.name}`);
        if (metadata.is_excel) toast.info(`Excel file with ${metadata.sheet_count} sheet(s)`);
        if (metadata.has_tables) toast.info('Table structure detected - will be preserved in export');
        // Auto-open preview for PDF (Phrase behaviour — preview visible by default)
        if (fileFormat === 'pdf') setShowPreview(true);
      }
    } catch (error) {
      toast.error(detailToMessage(error.response?.data?.detail, error.message || 'Failed to upload document'));
    } finally { setLoading(false); }
  };

  const applySourceFormattingToTarget = useCallback((sourceHtml, targetText) => {
    if (!sourceHtml || !targetText) return targetText;
    const parser = new DOMParser();
    const doc = parser.parseFromString(sourceHtml, 'text/html');

    // Extract dominant formatting from first formatted text node
    const dominant = {};
    function extractDominant(node, inherited = {}) {
      if (Object.keys(dominant).length > 0) return;
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        Object.assign(dominant, inherited);
        return;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const fmt = { ...inherited };
        const tag = node.tagName.toLowerCase();
        if (tag === 'b' || tag === 'strong') fmt.bold = true;
        if (tag === 'i' || tag === 'em') fmt.italic = true;
        if (tag === 'u') fmt.underline = true;
        const style = node.getAttribute('style') || '';
        const colorMatch = style.match(/(?<![a-z-])color:\s*([^;]+)/i);
        if (colorMatch) fmt.color = colorMatch[1].trim();
        const bgMatch = style.match(/background-color:\s*([^;]+)/i);
        if (bgMatch) fmt.backgroundColor = bgMatch[1].trim();
        const sizeMatch = style.match(/font-size:\s*([^;]+)/i);
        if (sizeMatch) fmt.fontSize = sizeMatch[1].trim();
        const familyMatch = style.match(/font-family:\s*([^;]+)/i);
        if (familyMatch) fmt.fontFamily = familyMatch[1].trim();
        for (const child of node.childNodes) extractDominant(child, fmt);
      }
    }

    extractDominant(doc.body);
    if (Object.keys(dominant).length === 0) return targetText;

    // Apply dominant formatting as wrapper around entire translated text
    let html = targetText;
    const styles = [];
    if (dominant.color) styles.push(`color: ${dominant.color}`);
    if (dominant.backgroundColor) styles.push(`background-color: ${dominant.backgroundColor}`);
    if (dominant.fontSize) styles.push(`font-size: ${dominant.fontSize}`);
    if (dominant.fontFamily) styles.push(`font-family: ${dominant.fontFamily}`);
    if (dominant.underline) html = `<u>${html}</u>`;
    if (dominant.italic) html = `<i>${html}</i>`;
    if (dominant.bold) html = `<b>${html}</b>`;
    if (styles.length > 0) html = `<span style="${styles.join('; ')}">${html}</span>`;
    return html;
  }, []);

  const buildInstructionsPrompt = useCallback(() => {
    const instructions = [];
    
    // From prebuilt state checkboxes
    if (prebuiltInstructions.formalTone) instructions.push('Maintain formal tone throughout');
    if (prebuiltInstructions.keepTechnical) instructions.push('Keep technical terms in English');
    if (prebuiltInstructions.preserveBrands) instructions.push('Preserve brand names and product names');
    if (prebuiltInstructions.genderNeutral) instructions.push('Use gender-neutral language');
    if (prebuiltInstructions.consistentTerminology) instructions.push('Maintain consistent terminology');
    if (prebuiltInstructions.industryTerms) instructions.push('Use industry-specific terminology');
    if (prebuiltInstructions.keepNumbers) instructions.push('Keep numbers and dates in original format');
    
    // From custom input
    if (customInstructions?.trim()) {
      instructions.push(customInstructions.trim());
    }
    
    // From project-level instructions
    if (selectedProject?.instructions?.prebuilt?.length > 0) {
      selectedProject.instructions.prebuilt.forEach(i => instructions.push(i));
    }
    if (selectedProject?.instructions?.custom?.trim()) {
      instructions.push(selectedProject.instructions.custom.trim());
    }
    
    // From selected instruction set
    if (selectedInstructionSet?.instructions) {
      instructions.push(selectedInstructionSet.instructions);
    }
    
    return instructions.join('. ');
  }, [selectedProject, selectedInstructionSet, prebuiltInstructions, customInstructions]);

  // Segment Navigation
  const scrollToSegment = useCallback((segmentId) => {
    const element = segmentRefs.current[segmentId];
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const focusIntoSegment = useCallback((segmentId) => {
    setTimeout(() => {
      const row = segmentRefs.current[segmentId];
      if (!row) return;
      const editable = row.querySelector('[contenteditable]');
      if (!editable) return;
      editable.focus();
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editable);
      selection.removeAllRanges();
      if (editable.textContent?.trim()) {
        selection.addRange(range);
      } else {
        range.collapse(false);
        selection.addRange(range);
      }
    }, 50);
  }, []);

  const navigateToNextSegment = useCallback(() => {
    if (segments.length === 0) return;
    const currentId = focusedSegmentId || activeSegment;
    if (!currentId) {
      const firstSeg = segments[0];
      setFocusedSegmentId(firstSeg.id);
      setActiveSegment(firstSeg.id);
      scrollToSegment(firstSeg.id);
      focusIntoSegment(firstSeg.id);
      return;
    }
    const currentIdx = segments.findIndex(s => s.id === currentId);
    if (currentIdx < segments.length - 1) {
      const nextSeg = segments[currentIdx + 1];
      setFocusedSegmentId(nextSeg.id);
      setActiveSegment(nextSeg.id);
      scrollToSegment(nextSeg.id);
      focusIntoSegment(nextSeg.id);
    }
  }, [segments, focusedSegmentId, activeSegment, scrollToSegment, focusIntoSegment]);

  const navigateToPreviousSegment = useCallback(() => {
    if (segments.length === 0) return;
    const currentId = focusedSegmentId || activeSegment;
    if (!currentId) {
      const lastSeg = segments[segments.length - 1];
      setFocusedSegmentId(lastSeg.id);
      setActiveSegment(lastSeg.id);
      scrollToSegment(lastSeg.id);
      focusIntoSegment(lastSeg.id);
      return;
    }
    const currentIdx = segments.findIndex(s => s.id === currentId);
    if (currentIdx > 0) {
      const prevSeg = segments[currentIdx - 1];
      setFocusedSegmentId(prevSeg.id);
      setActiveSegment(prevSeg.id);
      scrollToSegment(prevSeg.id);
      focusIntoSegment(prevSeg.id);
    }
  }, [segments, focusedSegmentId, activeSegment, scrollToSegment, focusIntoSegment]);

  const jumpToNextUnconfirmed = useCallback(() => {
    if (segments.length === 0) return;
    const currentIdx = segments.findIndex(s => s.id === activeSegment);
    const start = currentIdx >= 0 ? currentIdx + 1 : 0;
    for (let i = start; i < segments.length; i++) {
      if (!savedSegments.has(segments[i].id)) {
        setActiveSegment(segments[i].id);
        scrollToSegment(segments[i].id);
        focusIntoSegment(segments[i].id);
        return;
      }
    }
    for (let i = 0; i < start; i++) {
      if (!savedSegments.has(segments[i].id)) {
        setActiveSegment(segments[i].id);
        scrollToSegment(segments[i].id);
        focusIntoSegment(segments[i].id);
        return;
      }
    }
    toast.info('All segments confirmed!');
  }, [segments, activeSegment, savedSegments, scrollToSegment, focusIntoSegment]);

  const jumpToPrevUnconfirmed = useCallback(() => {
    if (segments.length === 0) return;
    const currentIdx = segments.findIndex(s => s.id === activeSegment);
    const start = currentIdx >= 0 ? currentIdx - 1 : segments.length - 1;
    for (let i = start; i >= 0; i--) {
      if (!savedSegments.has(segments[i].id)) {
        setActiveSegment(segments[i].id);
        scrollToSegment(segments[i].id);
        focusIntoSegment(segments[i].id);
        return;
      }
    }
    for (let i = segments.length - 1; i > start; i--) {
      if (!savedSegments.has(segments[i].id)) {
        setActiveSegment(segments[i].id);
        scrollToSegment(segments[i].id);
        focusIntoSegment(segments[i].id);
        return;
      }
    }
    toast.info('All segments confirmed!');
  }, [segments, activeSegment, savedSegments, scrollToSegment, focusIntoSegment]);

  const handleJumpToSegment = useCallback(() => {
    const num = parseInt(jumpSegmentNumber, 10);
    if (isNaN(num) || num < 1 || num > segments.length) {
      toast.error(`Please enter a number between 1 and ${segments.length}`);
      return;
    }
    const targetSeg = segments[num - 1];
    if (targetSeg) {
      setActiveSegment(targetSeg.id);
      setFocusedSegmentId(targetSeg.id);
      scrollToSegment(targetSeg.id);
      setShowJumpDialog(false);
      setJumpSegmentNumber('');
    }
  }, [jumpSegmentNumber, segments, scrollToSegment]);

  // Helper to strip HTML tags
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const stripDarkColors = (html) => {
    if (!html) return html;
    return html
      .replace(/color\s*:\s*#(?:0{3}|0{6})\b/gi, 'color: inherit')
      .replace(/color\s*:\s*rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)/gi, 'color: inherit')
      .replace(/color\s*:\s*black\b/gi, 'color: inherit');
  };

  // Translation functions - using correct CAT API endpoints
  const translateSegment = async (segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    if (!segment || !segment.source) return;

    setTranslating(true);
    const token = localStorage.getItem('token');

    try {
      // Strip HTML from source for AI call
      const plainSource = segment.sourceHtml ? stripHtmlTags(segment.sourceHtml) : segment.source;
      const projectInstructions = buildInstructionsPrompt();
      
      // Prepare glossary terms for API
      const glossaryForApi = glossaryTerms.map(t => ({
        source: t.source,
        target: t.target,
        context: t.context || '',
        case_sensitive: t.caseSensitive || false
      }));
      
      // Get context IDs from Universal Context System
      const contextIds = getContextIds();
      
      const res = await fetch(`${API_URL}/api/cat/translate-segment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          segment_id: segmentId,
          source_text: plainSource,
          source_lang: sourceLang || 'English',
          target_lang: targetLang || 'Hindi',
          project_id: selectedProject?.project_id || selectedProject?.id,
          instructions: projectInstructions,
          glossary_terms: glossaryForApi,
          document_id: documentInfo?.documentId || null,
          // Universal Context System IDs
          context_project_id: contextIds.projectId,
          context_instruction_id: contextIds.instructionId,
          context_glossary_id: contextIds.glossaryId,
          context_tm_id: contextIds.tmId
        })
      });
      const data = await res.json();

      if (res.ok) {
        const translatedText = data.target || data.translation;
        const formattedTarget = segment.sourceHtml 
          ? applySourceFormattingToTarget(segment.sourceHtml, translatedText)
          : translatedText;
        
        setSegments(prev => prev.map(s => 
          s.id === segmentId 
            ? { 
                ...s, 
                target: translatedText,
                targetHtml: formattedTarget,
                status: 'translated',
                is_tm_match: data.is_tm_match,
                tm_similarity: data.tm_similarity,
                contentVersion: (s.contentVersion || 0) + 1
              } 
            : s
        ));
        
        // Update navbar balance from API response
        updateFromApiResponse(data);
        
        if (data.is_tm_match) {
          toast.success(`TM Match (${data.tm_similarity}%) - FREE`);
        } else {
          toast.success(planInfo?.plan === 'payg' ? `Segment translated · ₹${(1 * 0.17).toFixed(2)}` : 'Segment translated');
        }
        triggerFirstFeatureReferral();
      } else {
        toast.error(detailToMessage(data.detail, 'Translation failed'));
      }
    } catch (error) {
      toast.error('Translation failed');
      console.error(error);
    } finally { setTranslating(false); }
  };

  // Translate all empty segments - using correct CAT API endpoint
  const translateAllSegments = async () => {
    if (!selectedProject) {
      toast.error('Please select a project first to use auto-translate');
      return;
    }
    
    const emptySegments = segments.filter(s => s.source?.trim() && !s.target?.trim());
    if (emptySegments.length === 0) {
      toast.info('All segments already translated');
      return;
    }

    const totalWords = emptySegments.reduce(
      (sum, s) => sum + ((s.source || '').trim().split(/\s+/).filter(Boolean).length), 0
    );
    setTranslating(true);
    setCatProgress({ done: 0, total: emptySegments.length, tmHits: 0, totalWords });
    const token = localStorage.getItem('token');

    const CHUNK_SIZE = 100;
    let totalTranslated = 0;
    const allTranslated = {};
    let lastChunkData = null;

    try {
      const allSegmentsForApi = segments.map(s => ({
        id: s.id,
        source: (s.sourceHtml ? stripHtmlTags(s.sourceHtml).trim() : '') || s.source,
        target: s.target || ''
      }));

      const chunks = [];
      for (let i = 0; i < allSegmentsForApi.length; i += CHUNK_SIZE) {
        chunks.push(allSegmentsForApi.slice(i, i + CHUNK_SIZE));
      }

      toast.info(`Translating ${emptySegments.length} segments in ${chunks.length} batch(es)...`);

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        setScanningSegmentIds(new Set(chunk.map(s => String(s.id))));
        try {
          const projectInstructions = buildInstructionsPrompt();
          
          // Prepare glossary terms for API
          const glossaryForApi = glossaryTerms.map(t => ({
            source: t.source,
            target: t.target,
            context: t.context || '',
            case_sensitive: t.caseSensitive || false
          }));
          
          // Get context IDs from Universal Context System
          const contextIds = getContextIds();
          
          const res = await fetch(`${API_URL}/api/cat/translate-all`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              segments: chunk,
              source_lang: sourceLang || 'English',
              target_lang: targetLang || 'Hindi',
              project_id: selectedProject?.project_id || selectedProject?.id,
              instructions: projectInstructions,
              glossary_terms: glossaryForApi,
              // Universal Context System IDs
              context_project_id: contextIds.projectId,
              context_instruction_id: contextIds.instructionId,
              context_glossary_id: contextIds.glossaryId,
              context_tm_id: contextIds.tmId
            })
          });
          const data = await res.json();

          if (res.ok) {
            (data.translated || []).forEach(t => {
              if (t.target && t.success !== false) {
                allTranslated[String(t.id)] = {
                  target: t.target,
                  is_tm_match: t.is_tm_match,
                  tm_similarity: t.tm_similarity,
                  is_repetition: t.is_repetition
                };
              }
            });
            
            lastChunkData = data;
            totalTranslated += data.total_translated || 0;
            const chunkDone = (data.translated || []).filter(t => t.target && t.success !== false).length;
            setCatProgress(prev => ({ done: prev.done + chunkDone, total: prev.total, tmHits: prev.tmHits + (data.tm_matches || 0) }));
            setScanningSegmentIds(new Set());
            if (chunks.length > 1) {
              let statusMsg = `Batch ${i + 1}/${chunks.length} complete`;
              if (data.tm_matches > 0) statusMsg += ` (${data.tm_matches} TM)`;
              toast.info(statusMsg, { duration: 1500 });
            }
          } else {
            if (res.status === 402) {
              toast.error('Insufficient credits/quota');
              break;
            }
          }
        } catch (chunkError) {
          console.error(`Chunk ${i + 1} failed:`, chunkError);
        }
      }

      setSegments(prev => prev.map(seg => {
        const trans = allTranslated[String(seg.id)];
        if (trans) {
          const formattedTarget = seg.sourceHtml 
            ? applySourceFormattingToTarget(seg.sourceHtml, trans.target)
            : trans.target;
          
          return {
            ...seg,
            target: trans.target,
            targetHtml: formattedTarget,
            status: trans.is_tm_match ? 'tm_match' : (trans.is_repetition ? 'repetition' : 'translated'),
            is_tm_match: trans.is_tm_match || false,
            tm_similarity: trans.tm_similarity,
            is_repetition: trans.is_repetition || false,
            contentVersion: (seg.contentVersion || 0) + 1
          };
        }
        return seg;
      }));
      
      const translatedCount = Object.keys(allTranslated).length;
      const missedIds = emptySegments.map(s => String(s.id)).filter(id => !allTranslated[id]);
      if (missedIds.length > 0) {
        console.warn('[translateAll] Segments with no translation applied:', missedIds);
      }
      if (translatedCount > 0) {
        toast.success(planInfo?.plan === 'payg' ? `Translated ${translatedCount} segments · ≈ ₹${(translatedCount * 0.17).toFixed(2)}` : `Translated ${translatedCount} segments`);
        if (lastChunkData) updateFromApiResponse(lastChunkData);
      } else {
        toast.warning('No segments were translated');
      }
    } catch (error) {
      toast.error('Batch translation failed');
      console.error(error);
    } finally {
      setTranslating(false);
      setScanningSegmentIds(new Set());
      setCatProgress({ done: 0, total: 0, tmHits: 0, totalWords: 0 });
    }
  };

  // Save All segments
  const handleSaveAll = () => {
    const segmentsWithTranslations = segments.filter(s => s.target?.trim());
    if (segmentsWithTranslations.length === 0) {
      toast.warning('No translated segments to save');
      return;
    }
    
    const newSavedSet = new Set(savedSegments);
    const newConfirmStatus = { ...segmentConfirmStatus };
    let newlySaved = 0;
    
    segmentsWithTranslations.forEach(seg => {
      if (!newSavedSet.has(seg.id)) {
        newSavedSet.add(seg.id);
        newlySaved++;
      }
      newConfirmStatus[seg.id] = 'confirmed';
    });
    
    setSavedSegments(newSavedSet);
    setSegmentConfirmStatus(newConfirmStatus);
    
    setSegments(prev => prev.map(s => 
      s.target?.trim() ? { ...s, status: 'confirmed' } : s
    ));
    
    toast.success(`Saved ${newlySaved} segments (${newSavedSet.size} total confirmed)`);
  };

  // Navigate to next unsaved segment
  const navigateToNextUnsavedSegment = (fromSegmentId) => {
    const currentIndex = segments.findIndex(s => s.id === fromSegmentId);

    for (let i = currentIndex + 1; i < segments.length; i++) {
      const seg = segments[i];
      if (!seg.target?.trim() || !savedSegments.has(seg.id)) {
        setActiveSegment(seg.id);
        scrollToSegment(seg.id);
        focusIntoSegment(seg.id);
        return;
      }
    }

    for (let i = 0; i < currentIndex; i++) {
      const seg = segments[i];
      if (!seg.target?.trim() || !savedSegments.has(seg.id)) {
        setActiveSegment(seg.id);
        scrollToSegment(seg.id);
        focusIntoSegment(seg.id);
        return;
      }
    }

    toast.info('All segments have been saved!');
  };

  // Save current segment and move to next
  const handleSaveAndNext = () => {
    if (!activeSegment) {
      const firstUnsaved = segments.find(s => {
        const edit = segmentEditsRef.current[s.id];
        return (edit?.text || s.target)?.trim() && !savedSegments.has(s.id);
      });
      if (firstUnsaved) {
        confirmSegment(firstUnsaved.id);
        navigateToNextUnsavedSegment(firstUnsaved.id);
      }
      return;
    }

    const segment = segments.find(s => s.id === activeSegment);
    const edit = segmentEditsRef.current[activeSegment];
    const currentTarget = edit?.text || segment?.target;
    if (currentTarget?.trim()) {
      if (!savedSegments.has(activeSegment)) {
        confirmSegment(activeSegment);
      }
      navigateToNextUnsavedSegment(activeSegment);
    } else {
      toast.warning('Please enter a translation before saving');
    }
  };

  // Save to Professional TM on segment confirmation
  const saveToProTm = async (segment, status = 'confirmed') => {
    if (!segment?.source?.trim() || !segment?.target?.trim() || !selectedProject) return;
    
    const token = localStorage.getItem('token');
    try {
      await fetch(`${PRO_API}/tm/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_text: segment.source,
          target_text: segment.target,
          source_lang: selectedProject.source_language || 'English',
          target_lang: selectedProject.target_language || 'Hindi',
          project_id: selectedProject.project_id || selectedProject.id,
          segment_id: segment.id,
          segment_status: status
        })
      });
    } catch (error) { console.error('Failed to save to TM:', error); }
  };

  // Confirm/Save segment (toggle checkmark)
  const confirmSegment = (segmentId) => {
    // Capture edit BEFORE syncSegmentEdits clears the ref
    let edit = segmentEditsRef.current[segmentId];
    // DOM fallback: debounce may not have fired yet (< 500ms since typing)
    if (!edit) {
      const row = segmentRefs.current[segmentId];
      const editable = row?.querySelector('[contenteditable]');
      const domText = editable?.innerText?.trim();
      if (domText) {
        edit = { text: editable.innerText, html: editable.innerHTML };
        segmentEditsRef.current[segmentId] = edit;
      }
    }
    syncSegmentEdits();

    const segment = segments.find(s => s.id === segmentId);
    const currentTarget = edit?.text || segment?.target;

    if (!currentTarget?.trim()) {
      toast.warning('Please enter a translation before confirming');
      return;
    }
    
    setSavedSegments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(segmentId)) {
        newSet.delete(segmentId);
        setSegmentConfirmStatus(p => ({ ...p, [segmentId]: null }));
        toast.info(`Segment ${segmentId} unmarked`);
      } else {
        newSet.add(segmentId);
        setSegmentConfirmStatus(p => ({ ...p, [segmentId]: 'confirmed' }));
        toast.success(`Segment ${segmentId} confirmed`);
        // Save to Professional TM with latest content
        const updatedSegment = edit 
          ? { ...segment, target: edit.text, targetHtml: edit.html }
          : segment;
        saveToProTm(updatedSegment, 'confirmed');
      }
      return newSet;
    });
    
    // Update segment status
    if (edit) {
      setSegments(prev => prev.map(s => 
        s.id === segmentId 
          ? { ...s, target: edit.text, targetHtml: edit.html, status: 'confirmed' } 
          : s
      ));
      delete segmentEditsRef.current[segmentId];
    } else {
      setSegments(prev => prev.map(s => 
        s.id === segmentId ? { ...s, status: 'confirmed' } : s
      ));
    }
  };

  // Copy source to target
  const copySourceToTarget = (segmentId) => {
    const segment = segments.find(s => s.id === segmentId);
    if (!segment) return;
    setSegments(prev => prev.map(s =>
      s.id === segmentId ? { ...s, target: s.source, targetHtml: s.sourceHtml || s.source, status: 'edited' } : s
    ));
    toast.success('Source copied to target');
  };


  // Generate live document preview using mammoth
  const generatePreview = useCallback(async () => {
    if (!documentInfo || segments.length === 0) return;
    if (documentInfo.format === 'xlsx') {
      // XLSX - render as table
      const rows = {};
      segments.forEach(seg => {
        const row = seg.formatting?.row || 1;
        if (!rows[row]) rows[row] = [];
        const targetText = seg.target?.trim() || seg.source;
        const srcHtml = seg.sourceHtml && seg.sourceHtml !== seg.source ? seg.sourceHtml : null;
        let cellStyle = 'border:1px solid #ddd;padding:6px 10px;min-width:80px;';
        if (srcHtml) {
          const bgMatch = srcHtml.match(/background-color:([^;'"]+)/);
          const colorMatch = srcHtml.match(/(?<!background-)color:([^;'"]+)/);
          const boldMatch = srcHtml.includes('font-weight:bold');
          if (bgMatch) cellStyle += `background-color:${bgMatch[1]};`;
          if (colorMatch) cellStyle += `color:${colorMatch[1]};`;
          if (boldMatch) cellStyle += 'font-weight:bold;';
        }
        rows[row].push(`<td style="${cellStyle}">${targetText}</td>`);
      });
      const tableHtml = Object.values(rows).map(cells => `<tr>${cells.join('')}</tr>`).join('');
      setPreviewHtml(`<table style="border-collapse:collapse;width:100%;font-size:13px">${tableHtml}</table>`);
      return;
    }
    if (documentInfo.format === 'pptx') {
      // PPTX - render slide-by-slide
      const slides = {};
      segments.forEach(seg => {
        const slide = seg.formatting?.slide || 1;
        if (!slides[slide]) slides[slide] = [];
        const targetText = seg.target?.trim() || seg.source;
        const srcHtml = seg.sourceHtml && seg.sourceHtml !== seg.source ? seg.sourceHtml : null;
        let style = 'margin:4px 0 8px;';
        if (srcHtml) {
          const colorMatch = srcHtml.match(/(?<!background-)color:([^;'"]+)/);
          const sizeMatch = srcHtml.match(/font-size:([^;'"]+)/);
          const boldMatch = srcHtml.includes('font-weight:bold');
          if (colorMatch) style += `color:${colorMatch[1]};`;
          if (sizeMatch) style += `font-size:${sizeMatch[1]};`;
          if (boldMatch) style += 'font-weight:bold;';
        }
        slides[slide].push(`<p style="${style}">${targetText}</p>`);
      });
      const slidesHtml = Object.entries(slides).map(([num, paras]) => 
        `<div style="border:1px solid #ddd;border-radius:8px;padding:16px;margin-bottom:16px;background:#fafafa">
          <div style="font-size:10px;color:#999;margin-bottom:8px">Slide ${num}</div>
          ${paras.join('')}
        </div>`
      ).join('');
      setPreviewHtml(slidesHtml);
      return;
    }
    if (documentInfo.format !== 'docx') {
      const html = segments.map((seg) => {
        const text = seg.target?.trim() || seg.source;
        const style = seg.formatting?.is_heading ? 'font-size:18px;font-weight:bold;margin:12px 0 6px' : 'margin:4px 0';
        return `<p style="${style}">${text}</p>`;
      }).join('');
      setPreviewHtml(html);
      return;
    }
    setPreviewLoading(true);
    try {
      const html = segments.map((seg) => {
        const fmt = seg.formatting || {};
        const targetText = seg.target?.trim() || seg.targetHtml?.trim();

        // Build paragraph style from formatting metadata
        let paraStyle = 'margin:2px 0 10px;padding:0;';
        if (fmt.is_heading) paraStyle += 'font-size:18px;font-weight:bold;';
        if (fmt.bold) paraStyle += 'font-weight:bold;';
        if (fmt.italic) paraStyle += 'font-style:italic;';
        if (fmt.underline) paraStyle += 'text-decoration:underline;';
        if (fmt.color) paraStyle += `color:${fmt.color};`;
        if (fmt.font_size) paraStyle += `font-size:${fmt.font_size}pt;`;
        if (fmt.alignment === 'CENTER') paraStyle += 'text-align:center;';
        if (fmt.alignment === 'RIGHT') paraStyle += 'text-align:right;';

        // Use sourceHtml as color/format template, inject translated text
        let displayContent = '';
        if (targetText && seg.sourceHtml && seg.sourceHtml !== seg.source) {
          // Extract spans with inline styles from sourceHtml
          const parser = new DOMParser();
          const doc = parser.parseFromString(seg.sourceHtml, 'text/html');
          const allSpans = doc.querySelectorAll('span[style]');
          if (allSpans.length > 0) {
            // Use first span style for the whole translated text
            const firstStyle = allSpans[0].getAttribute('style');
            displayContent = `<span style="${firstStyle}">${targetText}</span>`;
          } else {
            displayContent = targetText;
          }
        } else if (targetText) {
          displayContent = targetText;
        } else {
          displayContent = `<span style="color:#aaa;font-style:italic">[${seg.source.substring(0, 60)}]</span>`;
        }

        return `<p style="${paraStyle}">${displayContent}</p>`;
      }).join('');

      setPreviewHtml(`<div style="font-family:-apple-system,Georgia,serif;line-height:1.8;color:#1a1a1a">${html}</div>`);
    } catch (e) {
      console.error('Preview error:', e);
      setPreviewHtml('<p style="color:red">Preview generation failed</p>');
    } finally {
      setPreviewLoading(false);
    }
  }, [segments, documentInfo]);

  // Export document with proper formatting preservation
  const handleExport = async () => {
    if (segments.length === 0) { 
      toast.error('No document to export'); 
      return; 
    }

    // Check for unsaved segments
    const translatedSegments = segments.filter(s => s.target?.trim());
    const unsavedCount = translatedSegments.filter(s => !savedSegments.has(s.id)).length;
    
    if (unsavedCount > 0) {
      toast.error(`Please save all segments before exporting. ${unsavedCount} segment(s) are unsaved.`, {
        action: { label: 'Save All', onClick: handleSaveAll }
      });
      return;
    }

    // Check if there are untranslated segments
    const untranslatedSegments = segments.filter(s => !s.target?.trim());
    if (untranslatedSegments.length > 0) {
      const confirmExport = window.confirm(
        `${untranslatedSegments.length} segment(s) have no translation. Export anyway with source text for those segments?`
      );
      if (!confirmExport) return;
    }

    const token = localStorage.getItem('token');
    setLoading(true);
    
    const exportFormat = documentInfo?.format || 'docx';
    const formatConfig = {
      docx: { mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extension: '.docx' },
      xlsx: { mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extension: '.xlsx' },
      pdf: { mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extension: '.docx' },
      pptx: { mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', extension: '.pptx' },
      txt: { mimeType: 'text/plain', extension: '.txt' }
    };
    const config = formatConfig[exportFormat] || formatConfig.docx;

    try {
      if (segments.length > 100) {
        toast.info(`Exporting ${segments.length} segments... This may take a moment.`);
      }
      
      const segmentsData = segments.map(s => ({
        id: s.id,
        source: s.source,
        target: s.target || '',
        targetHtml: s.targetHtml || s.target || '',
        formatting: s.formatting,
        paragraph_style: s.paragraph_style
      }));

      const documentId = documentInfo?.documentId;
      
      const res = await fetch(`${DOC_API}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          document_id: documentId || `temp_${Date.now()}`,
          segments: segmentsData,
          export_format: exportFormat
        })
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const originalName = documentInfo?.name?.replace(/\.[^.]+$/, '') || 'translation';

        // The backend decides the real output format (e.g. a PDF export may come
        // back as a PDF when in-place editing succeeds, or fall back to a DOCX).
        // Derive the extension from the response so the saved file is never
        // mislabeled — a mislabeled extension is what makes Word report
        // "unreadable content" / "error trying to open the file".
        let extension = config.extension;
        const disposition = res.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?[^"';]*?(\.[A-Za-z0-9]+)["']?/);
        if (match && match[1]) {
          extension = match[1].toLowerCase();
        } else {
          const ct = (res.headers.get('Content-Type') || '').toLowerCase();
          const ctMap = {
            'application/pdf': '.pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
            'text/plain': '.txt'
          };
          for (const [key, ext] of Object.entries(ctMap)) {
            if (ct.includes(key)) { extension = ext; break; }
          }
        }

        a.download = `${originalName}_translated${extension}`;
        
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        const displayFormat = extension.replace('.', '').toUpperCase();
        toast.success(`Document exported successfully as ${displayFormat}`);
        setShowExportFeedback(true);
        triggerExportReferral();

        if (documentInfo?.hasTableStructure) toast.info('Table structure preserved in export');
        if (documentInfo?.hasColorFormatting) toast.info('Color formatting preserved in export');
      } else {
        let errMsg = 'Export failed';
        try {
          const data = await res.json();
          errMsg = detailToMessage(data.detail, errMsg);
        } catch (_) { /* binary/empty error body */ }
        toast.error(errMsg);
      }
    } catch (error) {
      const msg = error?.message || '';
      toast.error(msg.includes('fetch') || msg.includes('network') ? 'Export failed — network error' : 'Export failed');
      console.error('Export error:', error);
    } finally { setLoading(false); }
  };

  // QA Check with correct API
  const runQACheck = async () => {
    if (!segments.length) { toast.error('No segments to check'); return; }
    
    setRunningQA(true);
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/cat/run-qa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          segments: segments.map(s => ({ id: s.id, source: s.source, target: s.target })),
          source_lang: selectedProject?.source_language || sourceLang || 'en',
          target_lang: selectedProject?.target_language || targetLang || 'hi'
        })
      });
      const data = await res.json();
      
      if (res.ok) {
        setQaReport(data);
        if (data.passed) {
          toast.success('QA passed! No critical issues found.');
        } else {
          toast.warning(`QA found ${data.critical || 0} critical issues`);
        }
      } else { toast.error(detailToMessage(data.detail, 'QA check failed')); }
    } catch (error) { toast.error('QA check failed'); }
    finally { setRunningQA(false); }
  };

  // Reset all segments and state
  const handleReset = () => {
    if (!window.confirm('Are you sure you want to reset all translations? This cannot be undone.')) return;
    
    setSegments([]);
    setSavedSegments(new Set());
    setSegmentConfirmStatus({});
    setSelectedSegmentIds(new Set());
    setActiveSegment(null);
    setDocumentInfo(null);
    setUploadedFileName(null);
    setQaReport(null);
    toast.info('All translations have been reset');
  };

  // Merge selected segments
  const handleMergeSelected = () => {
    if (selectedSegmentIds.size < 2) {
      toast.warning('Please select at least 2 consecutive segments to merge');
      return;
    }
    
    const selectedArr = Array.from(selectedSegmentIds);
    const indices = selectedArr.map(id => segments.findIndex(s => s.id === id)).sort((a, b) => a - b);
    
    // Check if consecutive
    for (let i = 1; i < indices.length; i++) {
      if (indices[i] - indices[i - 1] !== 1) {
        toast.error('Can only merge consecutive segments');
        return;
      }
    }
    
    const segmentsToMerge = indices.map(i => segments[i]);
    const mergedSource = segmentsToMerge.map(s => s.source).join(' ');
    const mergedTarget = segmentsToMerge.map(s => s.target || '').join(' ');
    const mergedSourceHtml = segmentsToMerge.map(s => s.sourceHtml || s.source).join(' ');
    
    const newSegments = [...segments];
    const firstIndex = indices[0];
    
    newSegments[firstIndex] = {
      ...newSegments[firstIndex],
      source: mergedSource,
      sourceHtml: mergedSourceHtml,
      target: mergedTarget.trim() || '',
      status: mergedTarget.trim() ? 'translated' : 'draft'
    };
    
    // Remove merged segments (in reverse order to maintain indices)
    for (let i = indices.length - 1; i > 0; i--) {
      newSegments.splice(indices[i], 1);
    }
    
    setSegments(newSegments);
    setSelectedSegmentIds(new Set());
    toast.success(`Merged ${indices.length} segments`);
  };

  // Handle glossary file upload
  const handleGlossaryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    
    try {
      if (ext === 'csv') {
        const text = await file.text();
        Papa.parse(text, {
          header: true,
          complete: (results) => {
            const terms = results.data
              .filter(row => row['Source Term'] && row['Target Term'])
              .map(row => ({
                source: row['Source Term'],
                target: row['Target Term'],
                notes: row['Notes'] || ''
              }));
            
            if (terms.length > 0) {
              setGlossaryTerms(prev => [...prev, ...terms]);
              toast.success(`Loaded ${terms.length} glossary terms`);
            } else {
              toast.warning('No valid terms found in CSV');
            }
          },
          error: (error) => {
            toast.error('Failed to parse CSV: ' + error.message);
          }
        });
      } else if (ext === 'xlsx') {
        toast.info('Excel glossary upload - parsing...');
        // For xlsx, we'd need a library like xlsx or call the backend
        const formData = new FormData();
        formData.append('file', file);
        const token = localStorage.getItem('token');
        
        const res = await fetch(`${API_URL}/api/glossary/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          setGlossaryTerms(prev => [...prev, ...(data.terms || [])]);
          toast.success(`Loaded ${data.terms?.length || 0} glossary terms`);
        } else {
          toast.error('Failed to upload glossary');
        }
      }
    } catch (error) {
      toast.error('Failed to process glossary file');
      console.error(error);
    }
    
    e.target.value = '';
  };

  // Handle TMX file upload
  const handleTmxUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.tmx')) {
      toast.error('Please upload a .tmx file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit');
      return;
    }
    
    setUploadingTmx(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/pro/tm/upload-tmx`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setTmxFiles(prev => [...prev, { name: file.name, entries: data.entries || 0 }]);
        toast.success(`TMX file uploaded: ${data.entries || 0} translation units`);
      } else {
        const data = await res.json();
        toast.error(detailToMessage(data.detail, 'Failed to upload TMX file'));
      }
    } catch (error) {
      toast.error('Failed to upload TMX file');
      console.error(error);
    } finally {
      setUploadingTmx(false);
    }
    
    e.target.value = '';
  };

  // Add glossary term with proper API
  const handleAddGlossaryTerm = async () => {
    if (!newTermForm.source.trim() || !newTermForm.target.trim()) {
      toast.error('Source and target terms are required');
      return;
    }
    
    if (!selectedProject) {
      toast.error('No project selected');
      return;
    }
    
    const projectId = selectedProject.project_id || selectedProject.id;
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/glossary/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_term: newTermForm.source,
          target_term: newTermForm.target,
          notes: newTermForm.notes
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setGlossaryTerms(prev => [...prev, {
          id: data.id || Date.now(),
          source: newTermForm.source,
          target: newTermForm.target,
          notes: newTermForm.notes
        }]);
        setShowAddTermModal(false);
        setNewTermForm({ source: '', target: '', notes: '' });
        toast.success('Term added to glossary');
      } else {
        const data = await res.json();
        toast.error(detailToMessage(data.detail, 'Failed to add term'));
      }
    } catch (error) {
      console.error('Failed to add glossary term:', error);
      toast.error('Failed to add term');
    }
  };

  // Fetch glossary for project
  const fetchGlossary = async (projectId) => {
    if (!projectId) return;
    
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/glossary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      const terms = data.glossary?.terms || [];
      if (terms.length > 0) {
        setGlossaryTerms(prev => {
          // Only show toast and update if terms actually changed
          const prevKeys = prev.map(t => t.source).join(',');
          const newKeys = terms.map(t => t.source).join(',');
          if (prevKeys !== newKeys) {
            toast.info(`Loaded ${terms.length} glossary terms`);
            return terms;
          }
          return prev;
        });
      } else {
        setGlossaryTerms([]);
      }
    } catch (error) {
      console.error('Failed to fetch glossary:', error);
      setGlossaryTerms([]);
    }
  };

  // Save custom instructions for project
  const handleSaveInstructions = async () => {
    if (!selectedProject) return;
    
    setSavingInstructions(true);
    const token = localStorage.getItem('token');
    const projectId = selectedProject.project_id || selectedProject.id;
    
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          custom_instructions: editableInstructionText
        })
      });
      
      if (res.ok) {
        // Reload project data to refresh instructions
        const projectId = selectedProject?.project_id || selectedProject?.id;
        if (projectId) loadProjectData(projectId);
        setEditingInstructions(false);
        toast.success('Instructions saved successfully');
      } else {
        const data = await res.json();
        toast.error(detailToMessage(data.detail, 'Failed to save instructions'));
      }
    } catch (error) {
      console.error('Failed to save instructions:', error);
      toast.error('Failed to save instructions');
    } finally {
      setSavingInstructions(false);
    }
  };

  // Find glossary matches for active segment
  const findGlossaryMatchesForSegment = useCallback((segmentId) => {
    if (!glossaryTerms?.length) {
      setActiveSegmentGlossaryMatches([]);
      return;
    }
    
    const segment = segments.find(s => s.id === segmentId);
    if (!segment) {
      setActiveSegmentGlossaryMatches([]);
      return;
    }
    
    const sourceText = segment.source?.toLowerCase() || '';
    const matches = glossaryTerms.filter(term => {
      const sourceTerm = term.source?.toLowerCase() || '';
      return sourceText.includes(sourceTerm);
    });
    
    setActiveSegmentGlossaryMatches(matches);
  }, [glossaryTerms, segments]);

  // Fetch TM matches for active segment
  const fetchTmMatches = useCallback(async (sourceText, segmentId) => {
    if (!sourceText?.trim() || !selectedProject) return;
    
    setLoadingTmMatches(true);
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${PRO_API}/tm/matches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_text: sourceText,
          source_lang: selectedProject.source_language || 'English',
          target_lang: selectedProject.target_language || 'Hindi',
          project_id: selectedProject.project_id || selectedProject.id,
          include_global: true,
          min_similarity: 70
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setProTmMatches(data.matches || []);
      }
    } catch (error) {
      console.error('Failed to fetch TM matches:', error);
    } finally {
      setLoadingTmMatches(false);
    }
  }, [selectedProject]);

  // Load glossary and TM when project changes
  // Use primitive ID as dependency — avoids infinite loop from object reference changes
  const _glossaryProjectId = selectedProject?.project_id || selectedProject?.id || null;
  useEffect(() => {
    if (_glossaryProjectId) {
      fetchGlossary(_glossaryProjectId);
    }
  }, [_glossaryProjectId]);

  // Update TM and glossary matches when active segment changes
  useEffect(() => {
    if (activeSegment) {
      const segment = segments.find(s => s.id === activeSegment);
      if (segment?.source) {
        fetchTmMatches(segment.source, activeSegment);
      }
      findGlossaryMatchesForSegment(activeSegment);
    } else {
      setProTmMatches([]);
      setActiveSegmentGlossaryMatches([]);
    }
  }, [activeSegment, segments, fetchTmMatches, findGlossaryMatchesForSegment]);

  // Document preview — reset when document changes
  useEffect(() => {
    setDocPreviewHtml(null);
    setPreviewMode('target');
    setPreviewZoom(1.0);
    sourceHtmlMapRef.current = {};
    activeHighlightElRef.current = null;
    if (previewDocRef.current) previewDocRef.current.innerHTML = '';
  }, [documentInfo?.documentId]);

  // Document preview — fetch HTML from backend when preview toggled on
  useEffect(() => {
    if (!showPreview || !documentInfo?.documentId || !PREVIEW_FORMATS.has(documentInfo?.format)) return;
    if (docPreviewHtml) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setDocPreviewLoading(true);
    axios.get(`${API_URL}/api/cat/preview/${documentInfo.documentId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setDocPreviewHtml(res.data.html);
    }).catch(err => {
      console.error('Document preview fetch failed:', err);
    }).finally(() => {
      setDocPreviewLoading(false);
    });
  }, [showPreview, documentInfo?.documentId, documentInfo?.format, docPreviewHtml]);

  // Document preview — inject HTML directly into ref div, build source map, apply mode + highlight
  useEffect(() => {
    if (!previewDocRef.current) return;
    if (!docPreviewHtml) { previewDocRef.current.innerHTML = ''; sourceHtmlMapRef.current = {}; return; }
    previewDocRef.current.innerHTML = docPreviewHtml;
    const map = {};
    previewDocRef.current.querySelectorAll('[data-segment-id]').forEach(el => {
      const id = parseInt(el.dataset.segmentId, 10);
      // PDF overlay spans (.seg-overlay) are empty positioned hit-targets — never
      // inject text into them. The source map / text replacement uses the real
      // text-bearing element (.pdf-target-layer p for PDF, the element itself otherwise).
      if (id && !el.classList.contains('seg-overlay') && map[id] === undefined) {
        map[id] = el.innerHTML;
      }
      if (id) el.style.cursor = 'pointer';
    });
    sourceHtmlMapRef.current = map;
    segments.forEach(seg => {
      previewDocRef.current.querySelectorAll(`[data-segment-id="${seg.id}"]`).forEach(el => {
        if (el.classList.contains('seg-overlay')) return; // skip empty PDF overlays
        _applySegmentToPreview(el, seg, previewMode, map);
      });
    });
    // Restore highlight for any pre-selected segment
    if (activeSegment) _applyPreviewHighlight(activeSegment, previewDocRef.current, activeHighlightElRef);
  }, [docPreviewHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // Document preview — apply source/target text when mode changes or segments update (auto-translate)
  useEffect(() => {
    if (!docPreviewHtml || !previewDocRef.current) return;
    if (previewMode === 'source') {
      Object.entries(sourceHtmlMapRef.current).forEach(([id, src]) => {
        previewDocRef.current.querySelectorAll(`[data-segment-id="${id}"]`).forEach(el => {
          if (el.classList.contains('seg-overlay')) return; // skip empty PDF overlays
          el.innerHTML = src;
        });
      });
    } else {
      segments.forEach(seg => {
        previewDocRef.current.querySelectorAll(`[data-segment-id="${seg.id}"]`).forEach(el => {
          if (el.classList.contains('seg-overlay')) return; // skip empty PDF overlays
          _applySegmentToPreview(el, seg, previewMode, sourceHtmlMapRef.current);
        });
      });
    }
    // Re-assert highlight after innerHTML changes (class survives innerHTML change; re-add for safety)
    if (activeHighlightElRef.current) {
      activeHighlightElRef.current.classList.add('preview-highlight');
      activeHighlightElRef.current.style.outline = '2px solid #2563eb';
      activeHighlightElRef.current.style.backgroundColor = 'rgba(37,99,235,0.1)';
      activeHighlightElRef.current.style.borderRadius = '3px';
    }
  }, [previewMode, segments, docPreviewHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply preview-target-mode class for PDF/PPTX dual-layer switching (image vs. text)
  useEffect(() => {
    if (!previewDocRef.current) return;
    const isPptxOrPdf = documentInfo?.format === 'pdf' || documentInfo?.format === 'pptx';
    if (isPptxOrPdf && previewMode === 'target') {
      previewDocRef.current.classList.add('preview-target-mode');
    } else {
      previewDocRef.current.classList.remove('preview-target-mode');
    }
  }, [previewMode, documentInfo?.format, docPreviewHtml]);

  const handlePreviewClick = useCallback((e) => {
    const el = e.target.closest('[data-segment-id]');
    if (!el) return;
    const segId = parseInt(el.dataset.segmentId, 10);
    if (!segId) return;
    _applyPreviewHighlight(segId, previewDocRef.current, activeHighlightElRef);
    setActiveSegment(segId);
    scrollToSegment(segId);
  }, [scrollToSegment]);

  // Find & Replace
  const performFind = useCallback(() => {
    if (!findText.trim()) { setSearchResults([]); return; }
    const results = [];
    segments.forEach((seg, idx) => {
      const target = seg.target || '';
      const searchIn = caseSensitive ? target : target.toLowerCase();
      const searchFor = caseSensitive ? findText : findText.toLowerCase();
      if (searchIn.includes(searchFor)) {
        results.push({ segmentId: seg.id, segmentIndex: idx });
      }
    });
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
    if (results.length > 0) {
      setActiveSegment(results[0].segmentId);
      scrollToSegment(results[0].segmentId);
    }
    toast.info(`Found ${results.length} matches`);
  }, [findText, caseSensitive, segments, scrollToSegment]);

  const replaceCurrentMatch = useCallback(() => {
    if (currentResultIndex < 0 || !searchResults[currentResultIndex]) return;
    const result = searchResults[currentResultIndex];
    setSegments(prev => prev.map(s => {
      if (s.id === result.segmentId) {
        const regex = new RegExp(findText, caseSensitive ? 'g' : 'gi');
        return { ...s, target: (s.target || '').replace(regex, replaceText), status: 'edited' };
      }
      return s;
    }));
    toast.success('Replaced');
  }, [currentResultIndex, searchResults, findText, replaceText, caseSensitive]);

  const replaceAllMatches = useCallback(() => {
    if (searchResults.length === 0) return;
    setSegments(prev => prev.map(s => {
      if (searchResults.some(r => r.segmentId === s.id)) {
        const regex = new RegExp(findText, caseSensitive ? 'g' : 'gi');
        return { ...s, target: (s.target || '').replace(regex, replaceText), status: 'edited' };
      }
      return s;
    }));
    toast.success(`Replaced ${searchResults.length} occurrences`);
    setSearchResults([]);
    setCurrentResultIndex(-1);
  }, [searchResults, findText, replaceText, caseSensitive]);

  // Split segment
  const handleSplitSegment = useCallback(() => {
    if (!splitSegmentId) return;
    const segment = segments.find(s => s.id === splitSegmentId);
    if (!segment || splitPosition <= 0 || splitPosition >= segment.source.length) {
      toast.error('Invalid split position');
      return;
    }
    const firstPart = segment.source.substring(0, splitPosition);
    const secondPart = segment.source.substring(splitPosition);
    const segmentIdx = segments.findIndex(s => s.id === splitSegmentId);
    
    const newSegments = [
      ...segments.slice(0, segmentIdx),
      { ...segment, id: segment.id, source: firstPart, sourceHtml: firstPart, target: '', targetHtml: '', status: 'empty' },
      { id: `${segment.id}_split`, source: secondPart, sourceHtml: secondPart, target: '', targetHtml: '', status: 'empty' },
      ...segments.slice(segmentIdx + 1)
    ];
    
    setSegments(newSegments);
    setShowSplitModal(false);
    setSplitSegmentId(null);
    setSplitPosition(0);
    toast.success('Segment split successfully');
  }, [splitSegmentId, splitPosition, segments]);

  // Merge segments
  const handleMergeSegments = useCallback(() => {
    if (mergeSegmentIds.length < 2) {
      toast.error('Select at least 2 segments to merge');
      return;
    }
    const sorted = [...mergeSegmentIds].sort((a, b) => segments.findIndex(s => s.id === a) - segments.findIndex(s => s.id === b));
    const mergedSource = sorted.map(id => segments.find(s => s.id === id)?.source || '').join(' ');
    const mergedTarget = sorted.map(id => segments.find(s => s.id === id)?.target || '').join(' ');
    
    const firstIdx = segments.findIndex(s => s.id === sorted[0]);
    const newSegments = segments.filter(s => !sorted.slice(1).includes(s.id)).map(s => {
      if (s.id === sorted[0]) {
        return { ...s, source: mergedSource, sourceHtml: mergedSource, target: mergedTarget, targetHtml: mergedTarget, status: mergedTarget ? 'edited' : 'empty' };
      }
      return s;
    });
    
    setSegments(newSegments);
    setShowMergeModal(false);
    setMergeSegmentIds([]);
    toast.success('Segments merged successfully');
  }, [mergeSegmentIds, segments]);

  // Concordance search - Proper API
  const performConcordanceSearch = useCallback(async () => {
    if (!concordanceQuery.trim()) return;
    
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${PRO_API}/tm/concordance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          search_text: concordanceQuery,
          source_lang: selectedProject?.source_language,
          target_lang: selectedProject?.target_language,
          project_id: selectedProject?.project_id || selectedProject?.id,
          search_in: 'both',
          limit: 50
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConcordanceResults(data.results || []);
        if (data.results?.length === 0) {
          toast.info('No concordance matches found');
        } else {
          toast.success(`Found ${data.results.length} concordance matches`);
        }
      }
    } catch (error) {
      toast.error('Concordance search failed');
      console.error(error);
    }
  }, [concordanceQuery, selectedProject]);

  // Filter segments
  const qaIssueMap = useMemo(() => {
    if (!qaReport?.issues) return {};
    return qaReport.issues.reduce((acc, issue) => {
      const sid = issue.segment_id;
      if (!acc[sid]) acc[sid] = [];
      acc[sid].push(issue);
      return acc;
    }, {});
  }, [qaReport]);

  const segmentIndexMap = useMemo(() => {
    return segments.reduce((acc, seg, idx) => {
      acc[seg.id] = idx + 1;
      return acc;
    }, {});
  }, [segments]);

  const filteredSegments = segments.filter(seg => {
    if (filterStatus === 'empty') return !seg.target?.trim();
    if (filterStatus === 'translated') return seg.target?.trim() && !savedSegments.has(seg.id);
    if (filterStatus === 'confirmed') return savedSegments.has(seg.id);
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return seg.source?.toLowerCase().includes(query) || seg.target?.toLowerCase().includes(query);
    }
    return true;
  });

  // Calculate progress
  const progress = {
    total: segments.length,
    translated: segments.filter(s => s.target?.trim()).length,
    confirmed: savedSegments.size
  };

  // Persist panel state to localStorage
  useEffect(() => { try { localStorage.setItem('cat-left-panel', String(leftPanelOpen)); } catch {} }, [leftPanelOpen]);
  useEffect(() => { try { localStorage.setItem('cat-right-panel', String(rightPanelOpen)); } catch {} }, [rightPanelOpen]);

  // Keyboard shortcuts - Comprehensive
  useEffect(() => {
    const inInput = () => {
      const el = document.activeElement;
      return el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.contentEditable === 'true');
    };
    const handleKeyDown = (e) => {
      if (segments.length === 0) return;

      // Tab / Shift+Tab - Navigate segments (even from contentEditable)
      if (e.key === 'Tab') {
        e.preventDefault();
        if (e.shiftKey) navigateToPreviousSegment();
        else navigateToNextSegment();
        return;
      }

      // Ctrl+S - Save current segment
      if (e.ctrlKey && !e.shiftKey && e.key === 's') {
        e.preventDefault();
        if (activeSegment) confirmSegment(activeSegment);
        return;
      }

      // Ctrl+Shift+S - Save All
      if (e.ctrlKey && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        handleSaveAll();
        return;
      }

      // Ctrl+Enter - Save and Next
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        handleSaveAndNext();
        return;
      }

      // Ctrl+A - Auto-Translate All (only when not typing)
      if (e.ctrlKey && e.key === 'a' && !inInput()) {
        e.preventDefault();
        translateAllSegments();
        return;
      }

      // Ctrl+Q - Run QA
      if (e.ctrlKey && e.key === 'q') {
        e.preventDefault();
        runQACheck();
        return;
      }

      // Ctrl+E - Export
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        handleExport();
        return;
      }

      // Ctrl+G - Jump to segment
      if (e.ctrlKey && e.key === 'g') { e.preventDefault(); setShowJumpDialog(true); return; }

      // Ctrl+F - Find & Replace
      if (e.ctrlKey && e.key === 'f') { e.preventDefault(); setShowFindReplace(prev => !prev); return; }

      // Ctrl+ArrowDown/Up - Jump to next/prev unconfirmed segment
      if (e.ctrlKey && !e.shiftKey && e.key === 'ArrowDown') { e.preventDefault(); jumpToNextUnconfirmed(); return; }
      if (e.ctrlKey && !e.shiftKey && e.key === 'ArrowUp') { e.preventDefault(); jumpToPrevUnconfirmed(); return; }

      // ArrowDown / ArrowUp - Navigate when not in text input
      if (e.key === 'ArrowDown' && !inInput()) { e.preventDefault(); navigateToNextSegment(); return; }
      if (e.key === 'ArrowUp' && !inInput()) { e.preventDefault(); navigateToPreviousSegment(); return; }

      // Alt+Arrow Down/Up - Navigate
      if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); navigateToNextSegment(); return; }
      if (e.altKey && e.key === 'ArrowUp') { e.preventDefault(); navigateToPreviousSegment(); return; }

      // Ctrl+Shift+Arrow Down/Up - Navigate
      if (e.ctrlKey && e.shiftKey && e.key === 'ArrowDown') { e.preventDefault(); navigateToNextSegment(); return; }
      if (e.ctrlKey && e.shiftKey && e.key === 'ArrowUp') { e.preventDefault(); navigateToPreviousSegment(); return; }

      // F8 - Copy source to target
      if (e.key === 'F8' && activeSegment) { e.preventDefault(); copySourceToTarget(activeSegment); focusIntoSegment(activeSegment); return; }

      // ? - Show keyboard shortcuts
      if (e.key === '?' && !inInput()) { e.preventDefault(); setShowShortcutsModal(true); return; }

      // Enter - Enter edit mode on active segment (when not already in text)
      if (e.key === 'Enter' && !inInput() && activeSegment) {
        e.preventDefault();
        const row = segmentRefs.current[activeSegment];
        if (row) {
          const editable = row.querySelector('[contenteditable]');
          if (editable) {
            editable.focus();
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(editable);
            range.collapse(false);
            sel?.removeAllRanges();
            sel?.addRange(range);
          }
        }
        return;
      }

      // Escape - Exit edit mode / close dialogs
      if (e.key === 'Escape') {
        const active = document.activeElement;
        if (active?.contentEditable === 'true') active.blur();
        setShowJumpDialog(false);
        setShowFindReplace(false);
        setShowShortcutsModal(false);
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [segments, activeSegment, savedSegments, navigateToNextSegment, navigateToPreviousSegment, jumpToNextUnconfirmed, jumpToPrevUnconfirmed]);


  // Feature gating — after all hooks
  if (!catAccess.hasAccess) {
    return <FeatureLockScreen feature="cat_ai" requiredPlan={catAccess.upgradeTo || 'professional'} currentPlan={catAccess.currentPlan} />;
  }

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout} showSidebar={false} showFooter={false}>
      <T09nLoaderOverlay show={loading} message="Processing..." variant={isDarkMode ? 'dark' : 'light'} />
      <style>{`@keyframes cat-scan-line { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } } @keyframes cat-shimmer { 0% { transform: translateX(-200%); } 100% { transform: translateX(400%); } } @keyframes cat-overlay-in { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }`}</style>
      {/* Translation Processing Overlay */}
      {translating && catProgress.total > 0 && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', backgroundColor: isDarkMode ? 'rgba(10,15,26,0.92)' : 'rgba(255,255,255,0.95)' }}>
          <div style={{ background: isDarkMode ? '#111827' : '#ffffff', borderRadius: 16, padding: '40px 48px', width: '100%', maxWidth: 520, boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(0,0,0,0.12)', border: isDarkMode ? '1px solid #1e293b' : '1px solid #e5e7eb', animation: 'cat-overlay-in 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 44, height: 44, background: '#000', borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: isDarkMode ? '0 0 0 2px #ffffff' : '0 0 0 1px rgba(0,86,255,.30), 0 4px 20px rgba(0,86,255,.22)' }}>
                  <img src="/logo-marketing.png" alt="t09n.com" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 0, display: 'block' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 700, fontStyle: 'normal', fontSize: 18, color: isDarkMode ? '#ffffff' : '#000000', letterSpacing: '-0.3px', lineHeight: 1.1 }}>t09n<span style={{ color: isDarkMode ? '#ffffff' : '#333333', fontWeight: 700 }}>.com</span></span>
                  <span style={{ fontFamily: '"All Round Gothic", AllRoundGothic, -apple-system, BlinkMacSystemFont, sans-serif', marginTop: 3, fontSize: 11, color: isDarkMode ? '#ffffff' : '#000000', fontWeight: 700, fontStyle: 'oblique', lineHeight: 1.2, letterSpacing: '0.1px' }}>The Operating System For Translators</span>
                </div>
              </div>
            </div>
            <h2 style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#111827', margin: '0 0 6px' }}>t09n.com is processing your translation</h2>
            <p style={{ textAlign: 'center', fontSize: 13, color: isDarkMode ? '#64748b' : '#6b7280', margin: '0 0 28px' }}>
              {['Parsing document structure and extracting segments', 'Checking translation memory for cached matches', 'Applying translation engine to segments', 'Running quality checks and post-processing'][pipelineStage]}
            </p>
            <div style={{ position: 'relative', height: 6, background: isDarkMode ? '#1e293b' : '#f3f4f6', borderRadius: 6, marginBottom: 10, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${catProgress.total > 0 ? (catProgress.done / catProgress.total) * 100 : 0}%`, background: 'linear-gradient(90deg, #1d4ed8, #2563eb, #3b82f6)', borderRadius: 6, transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }} />
              <div style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)', animation: 'cat-shimmer 1.5s ease-in-out infinite' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 28, fontSize: 12 }}>
              <span style={{ color: isDarkMode ? '#64748b' : '#6b7280' }}>
                {catProgress.done} / {catProgress.total} segments
                {catProgress.totalWords > 0 && ` • ${catProgress.totalWords.toLocaleString()} words`}
              </span>
              <span style={{ color: '#2563eb', fontWeight: 600 }}>{catProgress.total > 0 ? Math.round((catProgress.done / catProgress.total) * 100) : 0}%</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
              {[
                { label: 'Document Analysis', Icon: FileText, idx: 0 },
                { label: 'TM Sync', Icon: Database, idx: 1 },
                { label: 'Translation Engine', Icon: Zap, idx: 2 },
                { label: 'Linguistic QA', Icon: CheckCircle, idx: 3 },
              ].map(({ label, Icon, idx }) => {
                const allDone = catProgress.done === catProgress.total && catProgress.total > 0;
                const active = !allDone && pipelineStage === idx;
                const done = allDone || pipelineStage > idx;
                return (
                  <div key={label} style={{ flex: 1, padding: '10px 6px', borderRadius: 12, textAlign: 'center', background: done ? (isDarkMode ? '#14532d' : '#f0fdf4') : active ? (isDarkMode ? '#1e293b' : '#f0f4ff') : (isDarkMode ? '#1e293b' : '#f9fafb'), border: `1px solid ${done ? (isDarkMode ? '#16a34a' : '#86efac') : active ? (isDarkMode ? '#60a5fa' : '#93c5fd') : (isDarkMode ? '#1e293b' : '#e5e7eb')}`, transition: 'all 0.4s ease' }}>
                    <Icon size={16} style={{ margin: '0 auto 6px', color: done ? (isDarkMode ? '#4ade80' : '#16a34a') : active ? (isDarkMode ? '#60a5fa' : '#2563eb') : (isDarkMode ? '#475569' : '#9ca3af'), display: 'block', transition: 'color 0.4s' }} />
                    <p style={{ fontSize: 11, fontWeight: 600, color: done ? (isDarkMode ? '#4ade80' : '#15803d') : active ? (isDarkMode ? '#60a5fa' : '#1d4ed8') : (isDarkMode ? '#475569' : '#9ca3af'), lineHeight: 1.3, margin: 0 }}>{label}</p>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 16, padding: '14px 20px', background: isDarkMode ? '#0f172a' : '#f9fafb', borderRadius: 12 }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <p style={{ fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#111827', margin: '0 0 2px' }}>{catProgress.done}</p>
                <p style={{ fontSize: 10, color: isDarkMode ? '#64748b' : '#6b7280', margin: 0 }}>Processed</p>
              </div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <p style={{ fontSize: 20, fontWeight: 700, color: '#2563eb', margin: '0 0 2px' }}>{catProgress.tmHits}</p>
                <p style={{ fontSize: 10, color: isDarkMode ? '#64748b' : '#6b7280', margin: 0 }}>TM Matches</p>
              </div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <p style={{ fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f1f5f9' : '#111827', margin: '0 0 2px' }}>{catProgress.total - catProgress.done}</p>
                <p style={{ fontSize: 10, color: isDarkMode ? '#64748b' : '#6b7280', margin: 0 }}>Remaining</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Context Selector Modal */}
      <ContextSelectorModal 
        isOpen={showContextModal} 
        onClose={() => setShowContextModal(false)} 
        variant="apple"
      />
      
      <div className={`flex h-[calc(100vh-40px)] ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-testid="apple-cat-tool-page">
        {/* Left Sidebar - Project & Document Info */}
        <div className={`border-r flex-shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out flex flex-col ${leftPanelOpen ? 'w-64' : 'w-12'} ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
          {leftPanelOpen ? (
          <>
            <div className="flex justify-end px-2 pt-2 flex-shrink-0">
              <button onClick={() => setLeftPanelOpen(false)} className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`} title="Collapse sidebar"><ChevronLeft size={16} /></button>
            </div>
            <div className="px-4 pb-4 pt-1 overflow-y-auto flex-1">
          {/* Global Context Bar - Compact version for sidebar */}
          {hasGlobalContext && (
            <div 
              className="mb-4 p-3 rounded-xl border cursor-pointer transition-all hover:shadow-md"
              style={{
                background: isDarkMode ? 'rgba(147, 51, 234, 0.1)' : 'linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)',
                borderColor: isDarkMode ? 'rgba(147, 51, 234, 0.3)' : 'rgba(147, 51, 234, 0.2)',
              }}
              onClick={() => setShowContextModal(true)}
              data-testid="cat-context-bar"
            >
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-purple-600" />
                <span className="text-xs font-semibold text-purple-700">Active Context</span>
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                {globalContextProject && (
                  <div className="flex items-center gap-1.5 truncate">
                    <FolderOpen size={12} className="text-purple-500 flex-shrink-0" />
                    <span className="truncate">{globalContextProject.name}</span>
                  </div>
                )}
                {globalContextInstruction && (
                  <div className="flex items-center gap-1.5 truncate">
                    <FileText size={12} className="text-blue-500 flex-shrink-0" />
                    <span className="truncate">{globalContextInstruction.name}</span>
                  </div>
                )}
                {globalContextGlossary && (
                  <div className="flex items-center gap-1.5 truncate">
                    <BookOpen size={12} className="text-emerald-500 flex-shrink-0" />
                    <span className="truncate">{globalContextGlossary.name}</span>
                  </div>
                )}
                {globalContextTM && (
                  <div className="flex items-center gap-1.5 truncate">
                    <Database size={12} className="text-amber-500 flex-shrink-0" />
                    <span className="truncate">{globalContextTM.name}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {!hasGlobalContext && (
            <div 
              className={`mb-4 p-3 rounded-xl border border-dashed border-purple-300 cursor-pointer transition-all ${isDarkMode ? 'hover:bg-purple-900/30' : 'hover:bg-purple-50'}`}
              onClick={() => setShowContextModal(true)}
              data-testid="cat-context-bar-empty"
            >
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-purple-500" />
                <span className="text-xs font-medium text-purple-600">Set Translation Context</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Click to apply project settings</p>
            </div>
          )}
          
          {/* Active Project Indicator (from GlobalContextBar) */}
          <div className="mb-6">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
              Active Project
            </label>
            {selectedProject ? (
              <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-900/20 border border-purple-800/50' : 'bg-purple-50 border border-purple-100'}`}>
                <div className="flex items-center gap-2">
                  <FolderOpen size={16} className="text-purple-600" />
                  <span className="text-sm font-medium text-purple-900">{selectedProject?.name}</span>
                </div>
                <span className="text-xs text-purple-600 block mt-1">
                  {selectedProject?.source_language} → {selectedProject?.target_language}
                </span>
              </div>
            ) : (
              <div 
                onClick={() => setShowContextModal(true)}
                className={`p-3 rounded-xl cursor-pointer transition-colors ${isDarkMode ? 'bg-amber-900/20 border border-amber-800/50 hover:bg-amber-900/30' : 'bg-amber-50 border border-amber-200 hover:bg-amber-100'}`}
              >
                <div className="flex items-center gap-2 text-amber-700">
                  <FolderOpen size={16} />
                  <span className="text-sm font-medium">No project selected</span>
                </div>
                <p className="text-xs text-amber-600 mt-1">Click here or use the context bar above to select a project</p>
              </div>
            )}
          </div>

          {/* Active Context Badges */}
          <ActiveContextBadgesApple variant="full" className="mb-6" showEmpty={false} />

          {/* Document Upload */}
          <div className="mb-6">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Document</label>
            {documentInfo ? (
              <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-900/20 border border-purple-800/50' : 'bg-purple-50 border border-purple-100'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={16} className="text-purple-600" />
                  <span className="text-sm font-medium text-purple-900 truncate">{documentInfo.name}</span>
                </div>
                <div className="text-xs text-purple-700">{documentInfo.segmentCount} segments</div>
                <button
                  onClick={() => { setSegments([]); setDocumentInfo(null); setSavedSegments(new Set()); setSegmentConfirmStatus({}); setQaReport(null); }}
                  className="mt-2 text-xs text-purple-600 hover:underline"
                  data-testid="clear-document-btn"
                >
                  Clear & upload new
                </button>
              </div>
            ) : (
              <div>
                <input ref={fileInputRef} type="file" accept=".docx,.xlsx,.txt,.csv,.pdf,.pptx,.ppt,.xliff,.xlf,.sdlxliff,.mqxliff,.txml" onChange={handleFileUpload} className="hidden" data-testid="file-input" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!selectedProject || loading}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-3 border-2 border-dashed rounded-xl text-sm text-gray-600 hover:border-purple-300 hover:text-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-gray-600 hover:bg-purple-900/30' : 'border-gray-200 hover:bg-purple-50'}`}
                  data-testid="upload-document-btn"
                >
                  <Upload size={18} />Upload Document
                </button>
                <p className="mt-2 text-xs text-gray-400 text-center">DOCX, XLSX, PDF, PPTX, TXT, CSV</p>
              </div>
            )}
          </div>

          {/* Progress Stats */}
          {segments.length > 0 && (
            <div className="mb-6">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Progress</label>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Translated</span>
                    <span className="font-medium">{progress.translated}/{progress.total}</span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress.total ? (progress.translated / progress.total) * 100 : 0}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Confirmed</span>
                    <span className="font-medium">{progress.confirmed}/{progress.total}</span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${progress.total ? (progress.confirmed / progress.total) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Actions</label>
            <button
              onClick={translateAllSegments}
              disabled={translating || segments.length === 0 || !selectedProject}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="translate-all-btn"
            >
              {translating ? <><Loader2 size={16} className="animate-spin" />Translating...</> : <><Play size={16} />Auto-Translate All (Ctrl+A)</>}
            </button>

            <button
              onClick={handleSaveAll}
              disabled={segments.length === 0 || progress.translated === 0}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="save-all-btn"
            >
              <CheckCheck size={16} />Save All (Ctrl+Shift+S)
            </button>

            <button
              onClick={runQACheck}
              disabled={runningQA || segments.length === 0 || progress.translated === 0}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="run-qa-btn"
            >
              {runningQA ? <><Loader2 size={16} className="animate-spin" />Running QA...</> : <><AlertCircle size={16} />Run QA Checks (Ctrl+Q)</>}
            </button>

            <button
              onClick={handleExport}
              disabled={loading || segments.length === 0}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gray-700 text-white rounded-xl text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="export-btn"
            >
              <Download size={16} />{documentInfo?.format === "pdf" ? "Export DOCX (Ctrl+E)" : documentInfo?.format ? `Export ${documentInfo.format.toUpperCase()} (Ctrl+E)` : "Export DOCX (Ctrl+E)"}
            </button>
            
            {/* Segment Operations */}
            <div className={`pt-2 mt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 block">Segment Operations</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => { if (activeSegment) { setSplitSegmentId(activeSegment); setShowSplitModal(true); } else { toast.info('Select a segment to split'); } }}
                  disabled={!activeSegment}
                  className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${isDarkMode ? 'text-amber-400 bg-amber-900/20 border border-amber-800/50 hover:bg-amber-900/30' : 'text-amber-600 bg-amber-50 border border-amber-200 hover:bg-amber-100'}`}
                  data-testid="split-segment-btn"
                >
                  <Scissors size={14} />Split
                </button>
                <button
                  onClick={() => setShowMergeModal(true)}
                  disabled={segments.length < 2}
                  className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50 ${isDarkMode ? 'text-blue-400 bg-blue-900/20 border border-blue-800/50 hover:bg-blue-900/30' : 'text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100'}`}
                  data-testid="merge-segments-btn"
                >
                  <GitMerge size={14} />Merge
                </button>
              </div>
            </div>
          </div>
          
          {/* QA Report Summary */}
          {qaReport && (
            <div className={`mt-4 p-3 rounded-xl ${qaReport.passed ? (isDarkMode ? 'bg-green-900/20 border border-green-800/50' : 'bg-green-50 border border-green-200') : (isDarkMode ? 'bg-red-900/20 border border-red-800/50' : 'bg-red-50 border border-red-200')}`} data-testid="qa-report-summary">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {qaReport.passed ? <CheckCircle size={16} className="text-green-600" /> : <AlertCircle size={16} className="text-red-600" />}
                  <span className={`text-sm font-medium ${qaReport.passed ? 'text-green-700' : 'text-red-700'}`}>
                    QA {qaReport.passed ? 'Passed' : 'Issues Found'}
                  </span>
                </div>
                {qaReport.quality_score !== undefined && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${qaReport.quality_score >= 90 ? (isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700') : qaReport.quality_score >= 70 ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700') : (isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')}`}>
                    {qaReport.quality_score}%
                  </span>
                )}
              </div>
              {!qaReport.passed && qaReport.issues && (
                <div className="space-y-1 text-xs">
                  {qaReport.critical > 0 && <div className="flex items-center gap-2 text-red-600"><span className="w-2 h-2 rounded-full bg-red-500"></span><span>{qaReport.critical} critical</span></div>}
                  {qaReport.warnings > 0 && <div className="flex items-center gap-2 text-amber-600"><span className="w-2 h-2 rounded-full bg-amber-500"></span><span>{qaReport.warnings} warnings</span></div>}
                </div>
              )}
            </div>
          )}
            </div>{/* end scrollable content */}
          </>
          ) : (
            <div className="flex flex-col items-center pt-3 gap-2">
              <button onClick={() => setLeftPanelOpen(true)} className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200' : 'text-gray-500 hover:bg-gray-100'}`} title="Expand sidebar"><ChevronRight size={16} /></button>
              <div className={`w-8 h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <button onClick={translateAllSegments} disabled={translating || segments.length === 0 || !selectedProject} className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${isDarkMode ? 'text-purple-400 hover:bg-purple-900/30' : 'text-purple-600 hover:bg-purple-50'}`} title="Auto-Translate All (Ctrl+A)">{translating ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />}</button>
              <button onClick={handleSaveAll} disabled={segments.length === 0 || progress.translated === 0} className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${isDarkMode ? 'text-green-400 hover:bg-green-900/30' : 'text-green-600 hover:bg-green-50'}`} title="Save All (Ctrl+Shift+S)"><CheckCheck size={18} /></button>
              <button onClick={runQACheck} disabled={runningQA || segments.length === 0 || progress.translated === 0} className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${isDarkMode ? 'text-amber-400 hover:bg-amber-900/30' : 'text-amber-600 hover:bg-amber-50'}`} title="Run QA Checks (Ctrl+Q)">{runningQA ? <Loader2 size={18} className="animate-spin" /> : <AlertCircle size={18} />}</button>
              <button onClick={handleExport} disabled={loading || segments.length === 0} className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`} title="Export DOCX (Ctrl+E)"><Download size={18} /></button>
            </div>
          )}
        </div>

        {/* Main Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className={`sticky top-0 border-b px-4 py-3 z-10 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {/* Language Selector */}
                <div className={`flex items-center gap-1 px-3 py-2 rounded-xl ${isDarkMode ? 'bg-purple-900/30 border border-purple-700' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100'}`} data-testid="language-selector">
                  <Languages size={14} className="text-purple-500" />
                  <select value={sourceLang} onChange={(e) => setSourceLang(e.target.value)} className={`text-sm font-medium bg-transparent border-none focus:outline-none cursor-pointer ${isDarkMode ? 'text-purple-300' : 'text-purple-900'}`} data-testid="source-lang-selector">
                    {LANGUAGE_OPTIONS.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                  </select>
                  <span className={`mx-1 ${isDarkMode ? 'text-purple-500' : 'text-purple-400'}`}>→</span>
                  <select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} className={`text-sm font-medium bg-transparent border-none focus:outline-none cursor-pointer ${isDarkMode ? 'text-purple-300' : 'text-purple-900'}`} data-testid="target-lang-selector">
                    {LANGUAGE_OPTIONS.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                  </select>
                </div>
                
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} size={16} />
                  <input
                    type="text"
                    placeholder="Search segments..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-9 pr-3 py-2 text-sm rounded-xl w-64 focus:outline-none focus:ring-2 ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500/20' : 'border border-gray-200 focus:border-purple-300 focus:ring-purple-100'}`}
                    data-testid="search-segments-input"
                  />
                </div>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`px-3 py-2 text-sm rounded-xl focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white focus:border-purple-500' : 'border border-gray-200 focus:border-purple-300'}`} data-testid="filter-status-select">
                  <option value="all">All segments</option>
                  <option value="empty">Empty</option>
                  <option value="translated">Translated</option>
                  <option value="confirmed">Confirmed</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <button onClick={() => setShowFindReplace(prev => !prev)} className={`p-2 rounded-xl transition-colors ${showFindReplace ? 'bg-purple-100 text-purple-600' : `text-gray-500 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}`} title="Find & Replace (Ctrl+F)" data-testid="find-replace-toggle">
                  <Replace size={18} />
                </button>
                <button onClick={() => setShowPreview(prev => !prev)} className={`p-2 rounded-xl transition-colors ${showPreview ? 'bg-purple-100 text-purple-600' : `text-gray-500 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}`} title="Live Preview (Ctrl+P)" data-testid="preview-toggle">
                  {showPreview ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                <button onClick={() => setShowJumpDialog(true)} className={`p-2 rounded-xl text-gray-500 transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} title="Jump to Segment (Ctrl+G)" data-testid="jump-segment-btn">
                  <SkipForward size={18} />
                </button>
                <button onClick={() => setShowShortcutsModal(true)} className={`p-2 rounded-xl text-gray-500 transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} title="Keyboard Shortcuts (?)" data-testid="shortcuts-btn">
                  <HelpCircle size={16} />
                </button>
                <div className="text-sm text-gray-500 ml-2">{filteredSegments.length} of {segments.length} segments</div>
              </div>
            </div>
            {/* Find & Replace Panel */}
            {showFindReplace && (
              <div className={`mt-3 p-3 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`} data-testid="find-replace-panel">
                <div className="flex items-center gap-3 mb-2">
                  <input type="text" placeholder="Find text..." value={findText} onChange={(e) => setFindText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && performFind()} className={`flex-1 px-3 py-2 text-sm rounded-lg border focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white focus:border-purple-500' : 'border-gray-200 focus:border-purple-300'}`} data-testid="find-input" />
                  <input type="text" placeholder="Replace with..." value={replaceText} onChange={(e) => setReplaceText(e.target.value)} className={`flex-1 px-3 py-2 text-sm rounded-lg border focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white focus:border-purple-500' : 'border-gray-200 focus:border-purple-300'}`} data-testid="replace-input" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input type="checkbox" checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} className="rounded" />Case sensitive
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{searchResults.length > 0 ? `${currentResultIndex + 1}/${searchResults.length}` : '0/0'}</span>
                    <button onClick={performFind} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="find-btn">Find</button>
                    <button onClick={replaceCurrentMatch} disabled={searchResults.length === 0} className={`px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-testid="replace-btn">Replace</button>
                    <button onClick={replaceAllMatches} disabled={searchResults.length === 0} className={`px-3 py-1.5 text-sm rounded-lg disabled:opacity-50 ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-testid="replace-all-btn">Replace All</button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Keyboard Shortcuts Bar */}
            <div className={`mt-2 flex items-center gap-3 text-xs flex-wrap ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <span className="flex items-center gap-1"><Keyboard size={12} /> Shortcuts:</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Tab</kbd> Next</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Shift+Tab</kbd> Prev</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Enter</kbd> Edit</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Esc</kbd> Exit edit</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Ctrl+S</kbd> Confirm</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Ctrl+Enter</kbd> Save &amp; Next</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Ctrl+A</kbd> Translate All</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Ctrl+Q</kbd> QA</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Ctrl+E</kbd> Export</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Ctrl+F</kbd> Find</span>
              <span><kbd className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Ctrl+G</kbd> Jump</span>
            </div>
          </div>

          {projectLoadError && (
            <div className="flex items-center justify-between px-4 py-3 mb-2 bg-amber-50 border border-amber-200 rounded-xl text-sm">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle size={16} />
                <span>{projectLoadError}</span>
              </div>
              <button
                onClick={() => loadProjectData(selectedProject?.project_id || selectedProject?.id)}
                className="flex items-center gap-1 px-3 py-1 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
              >
                <RefreshCw size={12} /> Retry
              </button>
            </div>
          )}

          {/* Segments + Document Preview */}
          <div className="flex-1 flex overflow-hidden">
            <div className={`${showPreview && PREVIEW_FORMATS.has(documentInfo?.format) ? 'w-[45%]' : 'flex-1'} overflow-y-auto ${isDarkMode ? 'divide-y divide-gray-700 bg-gray-900' : 'divide-y divide-gray-100 bg-white'}`}>
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-purple-600" size={32} /></div>
            ) : segments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                <FileText className={`mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} size={48} />
                <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>No document loaded</h3>
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Select a project and upload a document to start translating</p>
              </div>
            ) : filteredSegments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                <Search className={`mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} size={48} />
                <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>No matching segments</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Try adjusting your search or filter</p>
              </div>
            ) : (
              filteredSegments.map((segment, idx) => {
                const segmentIndex = segmentIndexMap[segment.id] || 0;
                const isActive = activeSegment === segment.id;
                const isSaved = savedSegments.has(segment.id);
                const issues = qaIssueMap[segment.id] || [];
                // C2 isolation: locked for non-PM/owner users if segment belongs to another
                const segOwner = segment.assigned_to;
                const userEmail = user?.email;
                const userRole = user?.role || 'translator';
                const isPmOrOwner = userRole === 'owner' || userRole === 'pm';
                const isLocked = segOwner && userEmail && segOwner !== userEmail && !isPmOrOwner;
                // C3 track-changes: PM editing a translator's confirmed segment
                const isReviewerEdit = segOwner && userEmail && segOwner !== userEmail && isPmOrOwner;
                const hasReviewerOverride = !!segment.translator_value && segment.translator_value !== segment.target;
                return (
                  <div
                    key={segment.id}
                    ref={el => segmentRefs.current[segment.id] = el}
                    onClick={() => {
                      if (isLocked) return;
                      setActiveSegment(segment.id);
                      _applyPreviewHighlight(segment.id, previewDocRef.current, activeHighlightElRef);
                    }}
                    className={`p-4 transition-all border-l-4 ${isLocked
                      ? `cursor-not-allowed opacity-60 border-l-gray-300 ${isDarkMode ? 'bg-gray-800/40' : 'bg-gray-50/70'}`
                      : `cursor-pointer ${isActive
                        ? (isDarkMode ? 'border-l-[#2563eb] bg-blue-900/20' : 'border-l-[#2563eb] bg-blue-50/70')
                        : `border-l-transparent ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}`}`}
                    data-testid={`segment-row-${segment.id}`}
                    style={{ position: 'relative', overflow: 'hidden' }}
                  >
                    {isLocked && (
                      <div className={`absolute top-1.5 right-2 flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`} title={`Assigned to ${segOwner}`}>
                        <Lock size={9} /> {segOwner?.split('@')[0]}
                      </div>
                    )}
                    {scanningSegmentIds.has(String(segment.id)) && (
                      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: '#2563eb', animation: 'cat-scan-line 1.8s ease-in-out infinite', zIndex: 2 }} />
                    )}
                    <div className="flex items-start gap-4">
                      <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {segmentIndex}
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <div className={`p-3 rounded-xl border-2 transition-all ${isActive ? 'border-blue-600' : (isDarkMode ? 'border-orange-700/50 bg-orange-900/20' : 'border-orange-100 bg-orange-50')}`}>
                          <div className={`text-[10px] font-medium mb-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`}>SOURCE ({sourceLang})</div>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-100' : ''}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(isDarkMode ? stripDarkColors(segment.sourceHtml || segment.source) : (segment.sourceHtml || segment.source)) }} />
                        </div>
                        <div className={`p-3 rounded-xl border-2 transition-all ${isActive
                          ? 'border-blue-600'
                          : isLocked
                            ? (isDarkMode ? 'border-gray-600/50 bg-gray-700/40' : 'border-gray-200 bg-gray-100')
                            : isSaved
                              ? (isDarkMode ? 'border-green-700/50 bg-green-900/20' : 'border-green-200 bg-green-50')
                              : (isDarkMode ? 'border-blue-700/50 bg-blue-900/20' : 'border-blue-100 bg-blue-50')}`}>
                          <div className={`text-[10px] font-medium mb-1 ${isLocked
                            ? (isDarkMode ? 'text-gray-500' : 'text-gray-400')
                            : isSaved
                              ? (isDarkMode ? 'text-green-400' : 'text-green-500')
                              : (isDarkMode ? 'text-blue-400' : 'text-blue-500')}`}>
                            TARGET ({targetLang}) {isSaved && !isLocked && '✓ Saved'}
                          </div>
                          {isLocked ? (
                            <div className={`text-sm min-h-[60px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {segment.target || <span className="italic opacity-50">Locked</span>}
                            </div>
                          ) : (
                          <EditableSegment
                            html={isDarkMode ? stripDarkColors(segment.targetHtml || segment.target || '') : (segment.targetHtml || segment.target || '')}
                            onChange={(text, html) => {
                              if (previewMode === 'target' && docPreviewHtml && previewDocRef.current) {
                                // Update every text-bearing element for this segment (PDF has a
                                // hidden source-layer overlay span + a visible target paragraph).
                                previewDocRef.current.querySelectorAll(`[data-segment-id="${segment.id}"]`).forEach(pEl => {
                                  if (pEl.classList.contains('seg-overlay')) return; // skip empty PDF overlay
                                  if (text.trim()) {
                                    pEl.innerHTML = text.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
                                  } else {
                                    pEl.innerHTML = `<span style="opacity:0.35;font-style:italic">${sourceHtmlMapRef.current[segment.id] || ''}</span>`;
                                  }
                                });
                              }
                              // C3: snapshot translator_value on first reviewer keystroke
                              if (isReviewerEdit && !segment.translator_value) {
                                setSegments(prev => prev.map(s =>
                                  s.id === segment.id ? { ...s, translator_value: s.target || '' } : s
                                ));
                              }
                              debouncedSegmentUpdate(segment.id, text, html);
                            }}
                            className={`w-full p-0 text-sm bg-transparent border-none resize-none focus:outline-none min-h-[60px] whitespace-pre-wrap ${isDarkMode ? 'text-gray-100' : ''}`}
                            placeholder="Enter translation..."
                            data-testid={`segment-target-${segment.id}`}
                          />
                          )}
                        </div>
                      </div>
                      {!isLocked && (
                      <div className="flex-shrink-0 flex flex-col items-center gap-2">
                        <button onClick={(e) => { e.stopPropagation(); translateSegment(segment.id); }} disabled={translating}
                          className={`p-2 rounded-lg disabled:opacity-50 ${isDarkMode ? 'text-purple-400 bg-purple-900/40 hover:bg-purple-900/60' : 'text-purple-600 bg-purple-100 hover:bg-purple-200'}`} title="Translate">
                          {translating ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); confirmSegment(segment.id); }} disabled={!segment.target?.trim()}
                          className={`p-2 rounded-lg disabled:opacity-50 ${isSaved ? 'text-white bg-green-500' : (isDarkMode ? 'text-green-400 bg-green-900/40 hover:bg-green-900/60' : 'text-green-600 bg-green-100 hover:bg-green-200')}`} title="Confirm (Ctrl+S)">
                          <Check size={16} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); copySourceToTarget(segment.id); }}
                          className={`p-2 rounded-lg ${isDarkMode ? 'text-gray-400 bg-gray-700 hover:bg-gray-600' : 'text-gray-500 bg-gray-100 hover:bg-gray-200'}`} title="Copy Source to Target (F8)">
                          <Copy size={16} />
                        </button>
                      </div>
                      )}
                    </div>
                    {/* C3 diff strip — shows when reviewer overrode translator's work */}
                    {hasReviewerOverride && (
                      <div className={`mt-2 flex items-start gap-2 px-3 py-2 rounded-xl text-xs border ${isDarkMode ? 'bg-amber-900/20 border-amber-700/50 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                        <span className="flex-shrink-0 font-semibold">Original:</span>
                        <span className={`flex-1 line-through opacity-70 truncate ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>{segment.translator_value}</span>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            const token = localStorage.getItem('token');
                            const pid = selectedProject?.project_id || selectedProject?.id;
                            try {
                              const res = await fetch(`${API_URL}/api/projects/${pid}/segments/${segment.id}/revert`, {
                                method: 'POST', headers: { Authorization: `Bearer ${token}` }
                              });
                              if (res.ok) {
                                const d = await res.json();
                                setSegments(prev => prev.map(s =>
                                  s.id === segment.id
                                    ? { ...s, target: d.segment?.target ?? s.translator_value, targetHtml: '', translator_value: undefined }
                                    : s
                                ));
                                toast.success('Reverted to translator version');
                              } else {
                                toast.error('Revert failed');
                              }
                            } catch { toast.error('Revert failed'); }
                          }}
                          className={`flex-shrink-0 px-2 py-0.5 rounded font-medium transition-colors ${isDarkMode ? 'bg-amber-800 hover:bg-amber-700 text-amber-200' : 'bg-amber-200 hover:bg-amber-300 text-amber-900'}`}
                        >Revert</button>
                      </div>
                    )}
                    {issues.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {issues.map((issue, i) => (
                          <span key={i} className={`text-xs px-2 py-1 rounded-full ${issue.severity === 'critical' ? (isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-600') : issue.severity === 'warning' ? (isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-600') : (isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600')}`}>
                            {issue.message}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
            </div>
            {showPreview && PREVIEW_FORMATS.has(documentInfo?.format) && (
              <div
                ref={previewContainerRef}
                className={`w-[55%] overflow-y-auto border-l ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
                onClick={handlePreviewClick}
              >
                {/* Sticky header — Source/Target toggle + Zoom controls (mirrors Phrase) */}
                {docPreviewHtml && !docPreviewLoading && (
                  <div className={`sticky top-0 z-10 flex items-center justify-between px-4 py-2 border-b flex-shrink-0 gap-3 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <span className={`text-xs font-semibold uppercase tracking-wider flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Document Preview</span>
                    {/* Source / Target toggle */}
                    <div className={`flex rounded-lg overflow-hidden border text-xs font-medium ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <button
                        onClick={e => { e.stopPropagation(); setPreviewMode('source'); }}
                        className={`px-3 py-1.5 transition-colors ${previewMode === 'source' ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      >
                        Source
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); setPreviewMode('target'); }}
                        className={`px-3 py-1.5 transition-colors border-l ${isDarkMode ? 'border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'} ${previewMode === 'target' ? '!bg-blue-600 !text-white' : ''}`}
                      >
                        Target
                      </button>
                    </div>
                    {/* Zoom controls — same UI pattern as Phrase */}
                    <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <button
                        onClick={e => { e.stopPropagation(); setPreviewZoom(z => Math.max(0.5, Math.round((z - 0.1) * 10) / 10)); }}
                        className={`w-6 h-6 flex items-center justify-center rounded font-bold transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                        title="Zoom out"
                      >−</button>
                      <button
                        onClick={e => { e.stopPropagation(); setPreviewZoom(1.0); }}
                        className={`min-w-[42px] h-6 text-center rounded transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                        title="Reset zoom"
                      >{Math.round(previewZoom * 100)}%</button>
                      <button
                        onClick={e => { e.stopPropagation(); setPreviewZoom(z => Math.min(2.0, Math.round((z + 0.1) * 10) / 10)); }}
                        className={`w-6 h-6 flex items-center justify-center rounded font-bold transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                        title="Zoom in"
                      >+</button>
                    </div>
                  </div>
                )}
                {docPreviewLoading && (
                  <div className="flex items-center justify-center h-full gap-3">
                    <Loader2 className="animate-spin text-purple-600" size={24} />
                    <span className="text-sm text-gray-500">Loading document preview...</span>
                  </div>
                )}
                {!docPreviewLoading && !docPreviewHtml && (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8 gap-3">
                    <FileText size={40} className="text-gray-300" />
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Document structure preview</p>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Upload a document to see live preview</p>
                  </div>
                )}
                {/* Ref-based content div — innerHTML set directly to avoid React reconciliation */}
                <div className="p-6 pb-20" style={{ display: docPreviewHtml && !docPreviewLoading ? 'block' : 'none' }}>
                  <div
                    ref={previewDocRef}
                    className={`max-w-[794px] mx-auto shadow-lg rounded-lg p-12 min-h-[1000px] preview-${documentInfo?.format || 'docx'} ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}
                    style={{
                      ...((['xlsx', 'xls', 'xliff', 'xlf', 'mqxliff', 'sdlxliff', 'txml', 'pptx'].includes(documentInfo?.format))
                        ? { fontFamily: 'system-ui, sans-serif', lineHeight: '1.5' }
                        : { fontFamily: 'Georgia, "Times New Roman", serif', lineHeight: '1.7' }),
                      zoom: previewZoom,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - TM, Glossary & Preview */}
        <div className={`border-l flex-shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out flex flex-col ${rightPanelOpen ? 'w-72' : 'w-10'} ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`flex ${rightPanelOpen ? 'justify-end' : 'justify-center'} px-2 pt-2 flex-shrink-0`}>
            <button onClick={() => setRightPanelOpen(v => !v)} className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`} title={rightPanelOpen ? 'Collapse panel' : 'Expand panel'}>{rightPanelOpen ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}</button>
          </div>
          {rightPanelOpen && (
          <div className="px-4 pb-4 pt-1 overflow-y-auto flex-1">
          {/* Collapsible Project Guide / Instructions Panel */}
          {selectedProject && (
            <div className="mb-6">
              <button
                onClick={() => setShowProjectGuide(!showProjectGuide)}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${isDarkMode ? 'bg-purple-900/30 border border-purple-700 hover:bg-purple-900/50' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 hover:from-purple-100 hover:to-indigo-100'}`}
                data-testid="project-guide-toggle"
              >
                <span className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                  <BookOpen size={16} className={isDarkMode ? 'text-purple-400' : 'text-purple-600'} />
                  Project Guide
                </span>
                <ChevronDown size={16} className={`text-purple-500 transition-transform ${showProjectGuide ? 'rotate-180' : ''}`} />
              </button>
              
              {showProjectGuide && (
                <div className={`mt-2 p-3 rounded-xl space-y-3 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-testid="project-guide-panel">
                  {/* Project Info */}
                  <div className={`pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Project</h4>
                    <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedProject.name}</p>
                    {selectedProject.instruction_name && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{selectedProject.instruction_name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                        {selectedProject.source_language || sourceLang} → {selectedProject.target_language || targetLang}
                      </span>
                    </div>
                  </div>
                  
                  {/* Active Instructions (from selector) */}
                  {selectedInstructionSet && (
                    <div className={`pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Selected Instruction</h4>
                      <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-green-900/20 border border-green-800/50' : 'bg-green-50 border border-green-100'}`}>
                        <p className="text-xs text-green-800 font-medium">{selectedInstructionSet.name}</p>
                        {selectedInstructionSet.instructions && (
                          <p className="text-xs text-green-700 mt-1 whitespace-pre-wrap">
                            {selectedInstructionSet.instructions}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Pre-built Instructions (checkboxes) */}
                  {Object.entries(prebuiltInstructions).some(([_, v]) => v) && (
                    <div className={`pb-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Applied Rules</h4>
                      <div className="space-y-1">
                        {Object.entries(prebuiltInstructions).filter(([_, v]) => v).map(([key]) => {
                          const labels = {
                            formalTone: 'Formal tone',
                            keepTechnical: 'Keep technical terms',
                            preserveBrands: 'Preserve brands',
                            genderNeutral: 'Gender-neutral',
                            consistentTerminology: 'Consistent terminology',
                            industryTerms: 'Industry terms',
                            keepNumbers: 'Keep numbers'
                          };
                          return (
                            <div key={key} className="flex items-center gap-1.5 text-xs text-purple-700">
                              <Check size={10} className="text-purple-500" />
                              {labels[key]}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Full Custom Instructions - Editable */}
                  <div className="pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Custom Instructions</h4>
                      {!editingInstructions && (
                        <button
                          onClick={() => {
                            setEditableInstructionText(selectedProject?.custom_instructions || customInstructions || '');
                            setEditingInstructions(true);
                          }}
                          className="text-xs text-purple-600 hover:text-purple-800"
                          data-testid="edit-instructions-btn"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    
                    {editingInstructions ? (
                      <div className="space-y-2">
                        <textarea
                          value={editableInstructionText}
                          onChange={(e) => setEditableInstructionText(e.target.value)}
                          placeholder="Enter custom instructions for translation..."
                          className="w-full p-2 text-xs border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 resize-none"
                          rows={4}
                          data-testid="instructions-textarea"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleSaveInstructions}
                            disabled={savingInstructions}
                            className="flex-1 text-xs py-1.5 px-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-1"
                            data-testid="save-instructions-btn"
                          >
                            {savingInstructions ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                            Save
                          </button>
                          <button
                            onClick={() => setEditingInstructions(false)}
                            className={`flex-1 text-xs py-1.5 px-3 text-gray-600 border rounded-lg ${isDarkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-800/50' : 'bg-yellow-50 border border-yellow-100'}`}>
                        {selectedProject?.custom_instructions || customInstructions ? (
                          <p className={`text-xs whitespace-pre-wrap max-h-32 overflow-y-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {selectedProject?.custom_instructions || customInstructions}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400 italic">No custom instructions set. Click Edit to add.</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Glossary Summary */}
                  <div className={`pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Glossary Terms:</span>
                      <span className="text-xs font-semibold text-green-600">{glossaryTerms.length} loaded</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Live Preview - sidebar (non-DOCX formats only; DOCX uses split panel) */}
          {showPreview && segments.length > 0 && documentInfo?.format !== 'docx' && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Live Preview</label>
                <button
                  onClick={generatePreview}
                  className="text-xs px-2 py-1 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
                  disabled={previewLoading}
                >
                  {previewLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              {previewHtml ? (
                <div
                  className={`max-h-[500px] overflow-y-auto p-4 rounded-xl border text-sm ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                  style={isDarkMode ? { fontFamily: 'Georgia, serif', lineHeight: '1.8' } : { fontFamily: 'Georgia, serif', lineHeight: '1.8', color: '#1a1a1a' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(isDarkMode ? stripDarkColors(previewHtml) : previewHtml) }}
                  data-testid="live-preview"
                />
              ) : (
                <div className={`p-4 rounded-xl border border-dashed text-center ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                  <Eye size={24} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-xs text-gray-400 mb-2">See your translation as a formatted document</p>
                  <button
                    onClick={generatePreview}
                    disabled={previewLoading}
                    className="text-xs px-3 py-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
                  >
                    {previewLoading ? 'Generating...' : 'Generate Preview'}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Translation Memory */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Translation Memory</label>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowConcordance(true)} className={`p-1 text-purple-600 rounded ${isDarkMode ? 'hover:bg-purple-900/30' : 'hover:bg-purple-50'}`} title="Concordance Search" data-testid="concordance-search-btn">
                  <Search size={14} />
                </button>
                <Database size={14} className="text-purple-500" />
              </div>
            </div>
            
            {loadingTmMatches ? (
              <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <Loader2 size={24} className="mx-auto mb-2 text-purple-400 animate-spin" />
                <p className="text-sm text-gray-500">Finding matches...</p>
              </div>
            ) : proTmMatches.length > 0 ? (
              <div className="space-y-2" data-testid="pro-tm-matches">
                {proTmMatches.map((match, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-xl cursor-pointer hover:shadow-sm transition-shadow ${isDarkMode ? 'bg-purple-900/20 border border-purple-800/50' : 'border border-purple-200 bg-purple-50'}`}
                    onClick={() => {
                      if (activeSegment) {
                        setSegments(prev => prev.map(s => s.id === activeSegment ? { ...s, target: match.target_text, targetHtml: match.target_text, status: 'tm_match', is_tm_match: true, tm_similarity: match.similarity } : s));
                        toast.success(`TM match applied (${match.similarity}%)`);
                      }
                    }}
                    data-testid={`tm-match-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-200 text-purple-700">{match.similarity}%</span>
                      {match.is_free && <span className="text-xs text-green-600 font-medium">FREE</span>}
                    </div>
                    <p className="text-xs text-gray-600 mb-1 line-clamp-2">{match.source_text}</p>
                    <p className="text-sm text-gray-900 font-medium line-clamp-2">{match.target_text}</p>
                  </div>
                ))}
              </div>
            ) : activeSegment ? (
              <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <Database size={24} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">No TM matches found</p>
              </div>
            ) : (
              <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <Database size={24} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">Select a segment to see TM matches</p>
              </div>
            )}
          </div>

          {/* Glossary (Term Base) - Scrollable Source→Target List */}
          <div className="mb-6" data-testid="glossary-section">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                <Book size={14} className="text-green-500" />Term Base (Glossary)
              </label>
              <span className="text-xs text-gray-400">{glossaryTerms.length} terms</span>
            </div>
            
            {selectedProject && (
              <div className="flex items-center gap-2 mb-3">
                <button 
                  onClick={() => {
                    const template = `Source Term,Target Term,Context,Case Sensitive,Notes
company,कंपनी,Business context,No,Official translation
revenue,राजस्व,Financial term,No,Use for all financial reports`;
                    const blob = new Blob([template], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'glossary_template.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className={`flex-1 text-xs py-2 px-3 rounded-lg ${isDarkMode ? 'bg-green-900/20 text-green-400 border border-green-800/50 hover:bg-green-900/30' : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'}`}
                  data-testid="glossary-download-template"
                >
                  <Download size={12} className="inline mr-1" />Template
                </button>
                <button
                  onClick={() => glossaryFileInputRef.current?.click()}
                  className={`flex-1 text-xs py-2 px-3 rounded-lg ${isDarkMode ? 'bg-green-900/20 text-green-400 border border-green-800/50 hover:bg-green-900/30' : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'}`}
                  data-testid="glossary-upload-btn"
                >
                  <Upload size={12} className="inline mr-1" />Upload
                </button>
                <input type="file" ref={glossaryFileInputRef} accept=".csv,.xlsx" className="hidden" onChange={handleGlossaryUpload} />
              </div>
            )}
            
            {/* Scrollable Glossary Term List - Source → Target */}
            {glossaryTerms.length > 0 ? (
              <div className={`max-h-48 overflow-y-auto border rounded-xl ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-testid="glossary-term-list">
                {glossaryTerms.map((term, idx) => (
                  <div 
                    key={idx} 
                    className={`px-3 py-2 flex items-center justify-between gap-2 cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-green-900/30' : 'hover:bg-green-50'} ${idx !== glossaryTerms.length - 1 ? `border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}` : ''} ${activeSegmentGlossaryMatches.some(m => m.source === term.source) ? (isDarkMode ? 'bg-green-900/30' : 'bg-green-100') : ''}`}
                    onClick={() => {
                      if (activeSegment) {
                        const segment = segments.find(s => s.id === activeSegment);
                        if (segment) {
                          const newTarget = (segment.target || '') + (segment.target ? ' ' : '') + term.target;
                          setSegments(prev => prev.map(s => s.id === activeSegment ? { ...s, target: newTarget, status: 'edited' } : s));
                          toast.success(`Inserted: ${term.target}`);
                        }
                      }
                    }}
                    title={`Click to insert "${term.target}"`}
                    data-testid={`glossary-term-row-${idx}`}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-gray-600 truncate block">{term.source}</span>
                    </div>
                    <ArrowRight size={12} className="text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-right">
                      <span className="text-xs font-medium text-green-700 truncate block">{term.target}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <Book size={24} className="mx-auto mb-2 text-gray-300" />
                <p className="text-xs text-gray-500">No glossary terms</p>
                <p className="text-xs text-gray-400 mt-1">Upload CSV or add terms manually</p>
              </div>
            )}
            
            {/* Active Segment Glossary Matches (highlighted) */}
            {activeSegmentGlossaryMatches.length > 0 && (
              <div className={`mt-2 p-2 rounded-lg ${isDarkMode ? 'bg-green-900/30 border border-green-800/50' : 'bg-green-100 border border-green-200'}`}>
                <p className="text-xs font-semibold text-green-800 mb-1">Matches in current segment:</p>
                <div className="flex flex-wrap gap-1">
                  {activeSegmentGlossaryMatches.map((term, idx) => (
                    <span 
                      key={idx}
                      className="text-xs px-2 py-0.5 bg-green-200 text-green-800 rounded-full cursor-pointer hover:bg-green-300"
                      onClick={() => {
                        if (activeSegment) {
                          const segment = segments.find(s => s.id === activeSegment);
                          if (segment) {
                            const newTarget = (segment.target || '') + (segment.target ? ' ' : '') + term.target;
                            setSegments(prev => prev.map(s => s.id === activeSegment ? { ...s, target: newTarget } : s));
                            toast.success('Glossary term inserted');
                          }
                        }
                      }}
                    >
                      {term.source} → {term.target}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <button onClick={() => setShowAddTermModal(true)} className={`w-full mt-2 text-xs py-2 px-3 rounded-lg ${isDarkMode ? 'text-green-400 border border-green-800/50 hover:bg-green-900/20' : 'text-green-700 border border-green-200 hover:bg-green-50'}`} data-testid="add-glossary-term-btn">
              <Plus size={12} className="inline mr-1" />Add Term
            </button>
          </div>
          </div>
          )}
        </div>
      </div>

      {/* Status Bar - Fixed at bottom */}
      <div className={`fixed bottom-0 left-0 right-0 border-t px-4 py-2 flex items-center justify-between z-50 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`} data-testid="cat-status-bar">
        <div className="flex items-center gap-6">
          {/* Segment Navigation */}
          <div className="flex items-center gap-2">
            <button 
              onClick={navigateToPreviousSegment} 
              disabled={!activeSegment || segments.length === 0}
              className={`p-1 text-gray-500 hover:text-purple-600 rounded disabled:opacity-30 disabled:cursor-not-allowed ${isDarkMode ? 'hover:bg-purple-900/30' : 'hover:bg-purple-50'}`}
              title="Previous segment"
            >
              <ChevronLeft size={18} />
            </button>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {activeSegment ? segments.findIndex(s => s.id === activeSegment) + 1 : 0} / {segments.length}
            </span>
            <button
              onClick={navigateToNextSegment}
              disabled={!activeSegment || segments.length === 0}
              className={`p-1 text-gray-500 hover:text-purple-600 rounded disabled:opacity-30 disabled:cursor-not-allowed ${isDarkMode ? 'hover:bg-purple-900/30' : 'hover:bg-purple-50'}`}
              title="Next segment"
            >
              <ChevronRight size={18} />
            </button>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1.5">
              <Hash size={14} className="text-gray-400" />
              <span className="text-gray-500">Segments:</span>
              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{segments.length}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Languages size={14} className="text-blue-400" />
              <span className="text-gray-500">Translated:</span>
              <span className="font-medium text-blue-600">{progress.translated}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle size={14} className="text-green-400" />
              <span className="text-gray-500">Saved:</span>
              <span className="font-medium text-green-600">{progress.confirmed}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Type size={14} className="text-gray-400" />
              <span className="text-gray-500">Words:</span>
              <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{segments.reduce((acc, s) => acc + (s.source?.split(/\s+/).filter(w => w).length || 0), 0)}</span>
            </span>
          </div>
        </div>
        
        {/* Right side - Language pair */}
        <div className="flex items-center gap-4">
          <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
            <Languages size={14} className="text-purple-500" />
            <span className="font-medium text-purple-700">{sourceLang?.substring(0, 2).toLowerCase() || 'en'}</span>
            <span className="text-purple-400">→</span>
            <span className="font-medium text-purple-700">{targetLang?.substring(0, 2).toLowerCase() || 'hi'}</span>
          </span>
        </div>
      </div>
      
      {/* Find & Replace Modal */}
      <ModalApple isOpen={showFindReplace} onClose={() => setShowFindReplace(false)} title="Find & Replace" size="lg">
        <div className="space-y-4">
          <div>
            <label className="apple-label">Find</label>
            <input
              type="text"
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && performFind()}
              className="apple-input"
              placeholder="Text to find..."
              data-testid="find-input"
            />
          </div>
          <div>
            <label className="apple-label">Replace with</label>
            <input
              type="text"
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              className="apple-input"
              placeholder="Replacement text..."
              data-testid="replace-input"
            />
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} className="apple-checkbox" />
            <span className="text-sm text-gray-600">Case sensitive</span>
          </label>
          {searchResults.length > 0 && (
            <div className="text-sm text-gray-600">
              Found {searchResults.length} matches • {currentResultIndex + 1} of {searchResults.length}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <ButtonApple variant="secondary" onClick={performFind} data-testid="find-btn">Find</ButtonApple>
          <ButtonApple variant="secondary" onClick={replaceCurrentMatch} disabled={currentResultIndex < 0} data-testid="replace-one-btn">Replace</ButtonApple>
          <ButtonApple onClick={replaceAllMatches} disabled={searchResults.length === 0} data-testid="replace-all-btn">Replace All</ButtonApple>
        </div>
      </ModalApple>

      {/* Keyboard Shortcuts Modal */}
      <ModalApple isOpen={showShortcutsModal} onClose={() => setShowShortcutsModal(false)} title="Keyboard Shortcuts" size="sm">
        <div className="space-y-1">
          {[
            ['Ctrl+Enter', 'Confirm + move to next segment'],
            ['Tab', 'Next segment'],
            ['Shift+Tab', 'Previous segment'],
            ['Ctrl+↓', 'Jump to next unconfirmed'],
            ['Ctrl+↑', 'Jump to previous unconfirmed'],
            ['F8', 'Copy source to target'],
            ['Enter', 'Enter edit mode'],
            ['Escape', 'Exit edit / close dialogs'],
            ['Ctrl+S', 'Confirm current segment'],
            ['Ctrl+Shift+S', 'Save all segments'],
            ['Ctrl+A', 'Auto-translate all'],
            ['Ctrl+Q', 'Run QA check'],
            ['Ctrl+E', 'Export document'],
            ['Ctrl+F', 'Find & Replace'],
            ['Ctrl+G', 'Jump to segment'],
            ['?', 'Show this help'],
          ].map(([key, desc]) => (
            <div key={key} className={`flex items-center justify-between py-2 border-b last:border-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{desc}</span>
              <kbd className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${isDarkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-gray-100 text-gray-700 border border-gray-300'}`}>{key}</kbd>
            </div>
          ))}
        </div>
      </ModalApple>

      {/* Jump to Segment Modal */}
      <ModalApple isOpen={showJumpDialog} onClose={() => setShowJumpDialog(false)} title="Jump to Segment" size="sm">
        <div>
          <label className="apple-label">Segment Number</label>
          <input
            type="number"
            value={jumpSegmentNumber}
            onChange={(e) => setJumpSegmentNumber(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleJumpToSegment()}
            className="apple-input"
            placeholder={`Enter 1-${segments.length}`}
            min="1"
            max={segments.length}
            data-testid="jump-input"
            autoFocus
          />
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <ButtonApple variant="secondary" onClick={() => setShowJumpDialog(false)}>Cancel</ButtonApple>
          <ButtonApple onClick={handleJumpToSegment} data-testid="jump-confirm-btn">Jump</ButtonApple>
        </div>
      </ModalApple>

      {/* Add Glossary Term Modal */}
      <ModalApple isOpen={showAddTermModal} onClose={() => { setShowAddTermModal(false); setNewTermForm({ source: '', target: '', notes: '' }); }} title="Add Glossary Term">
        <div className="space-y-4">
          <div>
            <label className="apple-label">Source Term *</label>
            <input
              value={newTermForm.source}
              onChange={(e) => setNewTermForm(prev => ({ ...prev, source: e.target.value }))}
              className="apple-input"
              placeholder="Term in source language"
              data-testid="new-term-source-input"
            />
          </div>
          <div>
            <label className="apple-label">Target Term *</label>
            <input
              value={newTermForm.target}
              onChange={(e) => setNewTermForm(prev => ({ ...prev, target: e.target.value }))}
              className="apple-input"
              placeholder="Translation in target language"
              data-testid="new-term-target-input"
            />
          </div>
          <div>
            <label className="apple-label">Notes (optional)</label>
            <input
              value={newTermForm.notes}
              onChange={(e) => setNewTermForm(prev => ({ ...prev, notes: e.target.value }))}
              className="apple-input"
              placeholder="Usage context, domain, etc."
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-6">
          <ButtonApple variant="secondary" onClick={() => { setShowAddTermModal(false); setNewTermForm({ source: '', target: '', notes: '' }); }}>Cancel</ButtonApple>
          <ButtonApple onClick={handleAddGlossaryTerm} data-testid="save-glossary-term-btn">Add Term</ButtonApple>
        </div>
      </ModalApple>

      {/* QA Details Modal */}
      <ModalApple isOpen={showQADetails && qaReport} onClose={() => setShowQADetails(false)} title="QA Report" size="2xl">
        <div className="mb-4 flex items-center gap-4">
          <span className={`text-sm font-bold px-3 py-1 rounded-full ${qaReport?.quality_score >= 90 ? (isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700') : qaReport?.quality_score >= 70 ? (isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700') : (isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')}`}>
            Score: {qaReport?.quality_score || 0}%
          </span>
          <span className="text-sm text-gray-500">{qaReport?.total_issues || 0} issues in {qaReport?.segments_with_issues || 0} segments</span>
        </div>
        
        {qaReport?.issues?.length > 0 ? (
          <div className="max-h-96 overflow-y-auto space-y-2">
            {qaReport.issues.map((issue, idx) => (
              <div
                key={idx}
                className={`p-3 rounded-xl cursor-pointer transition-colors ${
                  issue.severity === 'critical' ? (isDarkMode ? 'bg-red-900/20 border border-red-800/50 hover:bg-red-900/30' : 'bg-red-50 border border-red-200 hover:bg-red-100') :
                  issue.severity === 'warning' ? (isDarkMode ? 'bg-amber-900/20 border border-amber-800/50 hover:bg-amber-900/30' : 'bg-amber-50 border border-amber-200 hover:bg-amber-100') :
                  (isDarkMode ? 'bg-blue-900/20 border border-blue-800/50 hover:bg-blue-900/30' : 'bg-blue-50 border border-blue-200 hover:bg-blue-100')
                }`}
                onClick={() => { setActiveSegment(issue.segment_id); scrollToSegment(issue.segment_id); setShowQADetails(false); }}
                data-testid={`qa-issue-${issue.severity}-${idx}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-500">Segment #{issue.segment_id}</span>
                  <BadgeApple variant={issue.severity === 'critical' ? 'red' : issue.severity === 'warning' ? 'orange' : 'blue'} size="sm">
                    {issue.check?.replace(/_/g, ' ')}
                  </BadgeApple>
                </div>
                <p className="text-sm text-gray-800">{issue.message}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle size={48} className="mx-auto text-green-400 mb-3" />
            <p className="text-gray-600">No QA issues found!</p>
          </div>
        )}
      </ModalApple>

      {/* Concordance Modal */}
      <ModalApple isOpen={showConcordance} onClose={() => setShowConcordance(false)} title="Concordance Search" size="2xl">
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={concordanceQuery}
            onChange={(e) => setConcordanceQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && performConcordanceSearch()}
            className="apple-input flex-1"
            placeholder="Search for a word or phrase..."
            data-testid="concordance-input"
            autoFocus
          />
          <ButtonApple onClick={performConcordanceSearch} data-testid="concordance-search-submit">Search</ButtonApple>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {concordanceResults.length > 0 ? (
            <div className="space-y-2">
              {concordanceResults.map((result, idx) => (
                <div key={idx} className={`p-3 rounded-xl border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                    {result.matched_in_source && <BadgeApple variant="purple" size="sm">Source</BadgeApple>}
                    {result.matched_in_target && <BadgeApple variant="green" size="sm">Target</BadgeApple>}
                    <span>• {result.usage_count} uses</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{result.source_text}</p>
                  <p className="text-sm text-gray-900 font-medium">{result.target_text}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Search size={32} className="mx-auto mb-2 text-gray-300" />
              <p>Enter a search term to find concordance matches</p>
            </div>
          )}
        </div>
      </ModalApple>

      {/* Split Segment Modal */}
      <ModalApple isOpen={showSplitModal} onClose={() => { setShowSplitModal(false); setSplitSegmentId(null); setSplitPosition(0); }} title="Split Segment">
        {splitSegmentId && (
          <div className="space-y-4">
            <div>
              <label className="apple-label">Segment Text</label>
              <p className={`p-3 rounded-xl text-sm ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-800'}`}>
                {segments.find(s => s.id === splitSegmentId)?.source || ''}
              </p>
            </div>
            <div>
              <label className="apple-label">Split Position (character index)</label>
              <input
                type="number"
                value={splitPosition}
                onChange={(e) => setSplitPosition(parseInt(e.target.value) || 0)}
                min="1"
                max={(segments.find(s => s.id === splitSegmentId)?.source?.length || 1) - 1}
                className="apple-input"
                data-testid="split-position-input"
              />
            </div>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-4">
          <ButtonApple variant="secondary" onClick={() => { setShowSplitModal(false); setSplitSegmentId(null); setSplitPosition(0); }}>Cancel</ButtonApple>
          <ButtonApple onClick={handleSplitSegment} data-testid="split-confirm-btn">Split</ButtonApple>
        </div>
      </ModalApple>

      {/* Merge Segments Modal */}
      <ModalApple isOpen={showMergeModal} onClose={() => { setShowMergeModal(false); setMergeSegmentIds([]); }} title="Merge Segments" size="lg">
        <p className="text-sm text-gray-600 mb-4">Select consecutive segments to merge.</p>
        <div className={`max-h-60 overflow-y-auto border rounded-xl ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {segments.map((seg, idx) => (
            <label key={seg.id} className={`flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 ${isDarkMode ? 'hover:bg-gray-700 border-gray-700' : 'hover:bg-gray-50 border-gray-100'}`}>
              <input
                type="checkbox"
                checked={mergeSegmentIds.includes(seg.id)}
                onChange={(e) => {
                  if (e.target.checked) setMergeSegmentIds(prev => [...prev, seg.id].sort((a, b) => a - b));
                  else setMergeSegmentIds(prev => prev.filter(id => id !== seg.id));
                }}
                className="apple-checkbox"
              />
              <span className="text-xs font-medium text-gray-500 w-8">{idx + 1}</span>
              <span className="text-sm text-gray-800 truncate">{seg.source?.substring(0, 50)}...</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <ButtonApple variant="secondary" onClick={() => { setShowMergeModal(false); setMergeSegmentIds([]); }}>Cancel</ButtonApple>
          <ButtonApple onClick={handleMergeSegments} disabled={mergeSegmentIds.length < 2} data-testid="merge-confirm-btn">
            Merge {mergeSegmentIds.length} Segments
          </ButtonApple>
        </div>
      </ModalApple>

      {/* Glossary View Modal */}
      <ModalApple isOpen={showGlossaryModal} onClose={() => setShowGlossaryModal(false)} title="Term Base" size="2xl">
        {glossaryTerms.length > 0 ? (
          <div className={`overflow-hidden border rounded-xl ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <table className="w-full">
              <thead className={isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Target</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {glossaryTerms.map((term, idx) => (
                  <tr key={idx} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{term.source}</td>
                    <td className="px-4 py-3 text-sm text-green-700">{term.target}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{term.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Book size={32} className="mx-auto mb-2 text-gray-300" />
            <p>No glossary terms loaded</p>
          </div>
        )}
        <div className="text-sm text-gray-500 mt-4">{glossaryTerms.length} term(s) loaded</div>
      </ModalApple>

      {/* PDF Disclaimer Modal */}
      <PDFDisclaimer
        isOpen={showPdfDisclaimer}
        fileName={pendingFile?.name || ''}
        onProceed={handleDisclaimerProceed}
        onCancel={handleDisclaimerCancel}
        uploadContext="cat-tool"
      />

      {/* PPT Disclaimer Modal */}
      <PPTDisclaimer
        isOpen={showPptDisclaimer}
        fileName={pendingFile?.name || ''}
        onProceed={handleDisclaimerProceed}
        onCancel={handleDisclaimerCancel}
        uploadContext="cat-tool"
      />
      {showExportFeedback && (
        <ExportFeedbackModal
          feature="cat_tool"
          onClose={() => setShowExportFeedback(false)}
        />
      )}
      <ReferralToast
        toast={referralToast}
        onDismiss={() => setReferralToast(null)}
      />
    </MainLayoutApple>
  );
};

// Segment Row Component - Apple UI Style
const SegmentRowApple = React.forwardRef(({ 
  segment, 
  index, 
  isActive, 
  isSaved,
  confirmStatus,
  qaIssues,
  onSelect, 
  onTranslate, 
  onConfirm,
  onCopySource,
  onUpdate,
  translating 
}, ref) => {
  const targetRef = useRef(null);
  const { isDarkMode } = useTheme();

  const getStatusColor = () => {
    if (confirmStatus === 'confirmed' || isSaved) return isDarkMode ? 'border-l-green-500 bg-green-900/20' : 'border-l-green-500 bg-green-50';
    if (confirmStatus === 'edited') return isDarkMode ? 'border-l-amber-500 bg-amber-900/20' : 'border-l-amber-500 bg-amber-50';
    if (segment.is_tm_match) return isDarkMode ? 'border-l-purple-500 bg-purple-900/20' : 'border-l-purple-500 bg-purple-50';
    if (segment.target?.trim()) return isDarkMode ? 'border-l-blue-500 bg-blue-900/20' : 'border-l-blue-500 bg-blue-50';
    return isDarkMode ? 'border-l-transparent bg-gray-900' : 'border-l-transparent bg-white';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); onConfirm(); }
    if (e.key === 's' && e.ctrlKey && !e.shiftKey) { e.preventDefault(); onConfirm(); }
  };

  const handleInput = (e) => {
    const newText = e.currentTarget.textContent || '';
    onUpdate(newText);
  };

  return (
    <div 
      ref={ref}
      className={`grid grid-cols-[60px_1fr_1fr_100px] gap-4 p-4 border-l-4 transition-all ${getStatusColor()} ${isActive ? 'ring-2 ring-blue-200 ring-inset' : (isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50')}`}
      onClick={onSelect}
      data-segment-id={segment.id}
      data-testid={`segment-row-${segment.id}`}
    >
      {/* Segment Number */}
      <div className="flex flex-col items-center justify-start text-sm font-semibold text-gray-400">
        <span>{index}</span>
        <div className="flex flex-col gap-1 mt-1">
          {segment.hasColorFormatting && (
            <span className={`text-[10px] px-1 rounded ${isDarkMode ? 'text-purple-400 bg-purple-900/30' : 'text-purple-600 bg-purple-100'}`} title="Color formatting">
              <Palette size={10} />
            </span>
          )}
          {segment.is_tm_match && <BadgeApple variant="purple" size="sm">TM</BadgeApple>}
          {segment.is_repetition && <BadgeApple variant="orange" size="sm">REP</BadgeApple>}
          {segment.formatting?.is_table_cell && (
            <span className={`text-[10px] px-1 rounded ${isDarkMode ? 'text-blue-400 bg-blue-900/30' : 'text-blue-600 bg-blue-100'}`} title="Table cell">
              <Table size={10} />
            </span>
          )}
          {qaIssues?.length > 0 && <BadgeApple variant="red" size="sm">QA</BadgeApple>}
        </div>
      </div>

      {/* Source */}
      <div className={`text-sm leading-relaxed min-h-[44px] ${isDarkMode ? 'text-gray-100' : ''}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(isDarkMode ? stripDarkColors(segment.sourceHtml || segment.source) : (segment.sourceHtml || segment.source)) }} />

      {/* Target */}
      <div
        ref={targetRef}
        className={`text-sm leading-relaxed min-h-[44px] p-3 rounded-xl border border-transparent focus:border-blue-500 focus:outline-none transition-all ${isDarkMode ? 'bg-gray-700 focus:bg-gray-600 text-gray-100' : 'text-gray-900 bg-gray-50 focus:bg-white'}`}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={onSelect}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(isDarkMode ? stripDarkColors(segment.targetHtml || segment.target || '') : (segment.targetHtml || segment.target || '')) }}
        data-testid={`segment-target-${segment.id}`}
      />

      {/* Actions */}
      <div className="flex flex-col gap-1 items-center">
        <button
          onClick={(e) => { e.stopPropagation(); onCopySource(); }}
          className={`p-2 text-gray-400 hover:text-blue-600 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-blue-900/30' : 'hover:bg-blue-50'}`}
          title="Copy source"
          data-testid={`copy-source-${segment.id}`}
        >
          <Copy size={14} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onTranslate(); }}
          disabled={translating}
          className={`p-2 text-blue-600 rounded-lg transition-colors disabled:opacity-50 ${isDarkMode ? 'hover:bg-blue-900/30' : 'hover:bg-blue-50'}`}
          title="Translate"
          data-testid={`translate-segment-${segment.id}`}
        >
          <ArrowRight size={14} className={translating ? 'animate-pulse' : ''} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onConfirm(); }}
          disabled={!segment.target?.trim()}
          className={`p-2 rounded-lg transition-colors ${
            confirmStatus === 'confirmed' || isSaved
              ? (isDarkMode ? 'text-green-400 bg-green-900/30' : 'text-green-600 bg-green-100')
              : confirmStatus === 'edited'
              ? (isDarkMode ? 'text-amber-400 bg-amber-900/30' : 'text-amber-600 bg-amber-100')
              : (isDarkMode ? 'text-gray-400 hover:text-green-400 hover:bg-green-900/30' : 'text-gray-400 hover:text-green-600 hover:bg-green-50')
          }`}
          title="Confirm (Ctrl+S)"
          data-testid={`confirm-segment-${segment.id}`}
        >
          {isSaved ? <CheckCircle size={14} /> : <Check size={14} />}
        </button>
      </div>
    </div>
  );
});

SegmentRowApple.displayName = 'SegmentRowApple';

export default CATToolPageApple;
